import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import './App.css';

// Types
interface Product3D {
  id: number;
  variant_sku: string;
  base_sku: string;
  glb_path: string;
  product_name?: string;
  open_glb_path?: string | null;
}

interface Technique {
  id: number;
  technique_code: string;
  technique_name: string;
  print_area: string;
  position?: string;
  position_1_width?: number;
  position_1_height?: number;
  centroid_x_1?: number;
  centroid_y_1?: number;
}

interface PrintAreaData {
  min_y: number;
  max_y: number;
  angle_range: number;
  technique_code: string;
  print_area_markers: any[];
}

interface MarkerPoint {
  x: number;
  y: number;
  z: number;
}

interface MeshData {
  radius: number;
  centerX: number;
  centerZ: number;
  startHeight: number;
  endHeight: number;
  meshHeight: number;
  heightAxis: string;
}

interface MeshConfig {
  mesh_type: string;
  mesh_scale_height: number;
  mesh_scale_width: number;
  mesh_position_x: number;
  mesh_position_y: number;
  mesh_position_z: number;
  mesh_rotation_x: number;
  mesh_rotation_y: number;
  mesh_rotation_z: number;
  mesh_scale_top: number;
  mesh_scale_bottom: number;
  mesh_crop_top: number;
  mesh_crop_bottom: number;
  surface_offset: number;
  scale: number;
  print_area_data?: PrintAreaData;
  marker_points?: MarkerPoint[];
  mesh_data?: MeshData;
}

interface Centroid {
  position: string;
  sku: string;
  x: number;
  y: number;
  xMm: number;
  yMm: number;
  printAreaWidth: number;
  printAreaHeight: number;
  printArea: string;
  productHeight: number;
  productDiameter: number;
  isDirectMM: boolean;
}

interface CentroidResponse {
  success: boolean;
  count: number;
  centroids: Centroid[];
  product: {
    width: number;
    height: number;
    diameter: number;
  };
}

// Centroid-correctie: de berekende printpositie 10 mm laten zakken zodat hij
// overeenkomt met het werkelijke drukvlak (dop-naad / bodemmarge).
// Hoeveel het centroid moet zakken t.o.v. de berekende positie.
// Achtergrond: de printzone wordt in de viewer over de HELE modelhoogte gemapt,
// inclusief de dop/kraag van ~31 mm bovenaan, terwijl het echte drukvlak daar
// onder begint. Daardoor kwam het logo te hoog op het product. De nette oplossing
// is `mesh_crop_top` per product in de database zetten (31,3 / 147 = 0,213 voor
// 347); zolang dat er niet staat corrigeren we het hier.
// 29-07: op 0 gezet. De 10 mm compenseerde het oude centroids-endpoint dat
// yMm systematisch te hoog teruggaf; sinds de mm/px-unit-detectie in de
// backend klopt yMm en duwde deze drop het logo juist 10 mm te laag.
const CENTROID_DROP_MM = 0;

// Verlaging voor producten/technieken ZONDER eigen centroid-marker. DP-UV8 op 347
// heeft `position` en `print_area` niet gevuld in de database en de centroid heet
// 'mittig' terwijl de viewer 'midden' zoekt; dan valt de plaatsing terug op het
// midden van de printzone. Die zone loopt over de HELE modelhoogte, dop inbegrepen,
// dus komt het logo te hoog. Zodra het printvlak en de positie voor die technieken
// in de database staan, is deze correctie niet meer nodig.
const FALLBACK_DROP_MM = 30;

// Position colors for centroid markers
const POSITION_COLORS: Record<string, number> = {
  'boven': 0x00ff00,    // Green for top
  'midden': 0x0000ff,   // Blue for middle
  'zone1': 0xff00ff,    // Magenta for zone1
  'zone2': 0xffff00,    // Yellow for zone2
  'default': 0xff8800   // Orange for others
};

function App() {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const loaderRef = useRef<GLTFLoader | null>(null);
  // PF Concept: imprint-meshes per printvlak (areaId -> mesh/materiaal/lege textuur) + extra bezette vlakken
  const pfImprintsRef = useRef<Map<string, { mesh: THREE.Mesh; material: THREE.MeshStandardMaterial; blank: THREE.Texture | null }>>(new Map());
  const pfExtraAreasRef = useRef<Set<string>>(new Set());
  const pfCamAreaRef = useRef<string>('');
  const decalViewRef = useRef<string>(''); // textiel-decal: laatst gekozen kijkrichting (voor/achter/mouw)
  const techniqueMeshRef = useRef<THREE.Mesh | null>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const clockRef = useRef<THREE.Clock>(new THREE.Clock());
  const openModelRef = useRef<THREE.Group | null>(null);
  const openMixerRef = useRef<THREE.AnimationMixer | null>(null);
  const openActionsRef = useRef<THREE.AnimationAction[]>([]);
  const openLoadPromiseRef = useRef<Promise<void> | null>(null);
  const centroidMarkersRef = useRef<THREE.Object3D[]>([]);
  const baseScaleRef = useRef<number>(1.0);
  const hgWrapRef = useRef<THREE.Mesh | null>(null); // HG-laagtextuur rondom het product
  // indirectie: de postMessage-handler staat boven applyHgWrap in dit bestand
  const applyHgWrapRef = useRef<((dataUrl: string, w?: number, h?: number, ph?: number) => void) | null>(null);
  // wrap die binnenkwam voordat het model klaar was
  const pendingWrapRef = useRef<{ dataUrl: string; w?: number; h?: number; ph?: number; bo?: number; hu?: string; rm?: number; vw?: any; sp?: number } | null>(null);
  const hgGenRef = useRef(0); // generatie-teller tegen verweesde wrap-meshes
  // Schroefdop-animatie (single-file GLB, CapPivotAction). Laadt op frame 1 (dicht);
  // de Open/Sluit-knop speelt naar het juiste frame. Frames uit frames.csv: elke
  // spranz-variant open=68 / dicht=120 @ 24fps.
  const capActionRef = useRef<THREE.AnimationAction | null>(null);
  // De node die door de dop-animatie bewogen wordt; logo's met positie
  // "auf Deckel" worden hieraan gehangen zodat ze meedraaien bij Open/Close.
  const capNodeRef = useRef<THREE.Object3D | null>(null);
  const capClipDurRef = useRef<number>(0);
  const capTargetRef = useRef<number | null>(null);
  const CAP_OPEN_FRAME = 68;    // frames.csv: frame_open
  const CAP_TOTAL_FRAMES = 120; // frames.csv: frame_close (frame 1 = dop dicht)

  // State
  const [products, setProducts] = useState<Product3D[]>([]);
  const [selectedSku, setSelectedSku] = useState<string>('');
  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [selectedTechnique, setSelectedTechnique] = useState<string>('');
  const [loading, setLoading] = useState(false);
  // Cap-animatie UI-state
  const [hasCap, setHasCap] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [capBusy, setCapBusy] = useState(false);
  // Hydroglasur (HG) laag-menu — start: full color op één laag.
  // Basis voor uitbreiding: fill Pantone/glitter/goud/metallic, opacity, hoogte (max 8 lagen).
  type HgFill = 'fullcolor' | 'pantone' | 'glitter' | 'goud' | 'metallic';
  interface HgLayer { id: number; fill: HgFill; }
  const [hgLayers, setHgLayers] = useState<HgLayer[]>([{ id: 1, fill: 'fullcolor' }]);

  // Meld de parent (modal) wanneer het model laadt / klaar is (3D-loader overlay)
  useEffect(() => {
    try {
      if (loading) {
        window.parent.postMessage({ type: 'magic_3d_loading' }, '*');
      } else if (loadedSkuRef.current) {
        window.parent.postMessage({ type: 'magic_3d_ready' }, '*');
      }
    } catch (e) { /* geen parent */ }
  }, [loading]);
  const [status, setStatus] = useState('Selecteer een product');
  const [meshConfig, setMeshConfig] = useState<MeshConfig | null>(null);
  // Synchrone spiegel van meshConfig: createCentroidMarker draait in dezelfde
  // load-flow als setMeshConfig en las anders nog null (stale state), waardoor
  // mesh_crop_top/bottom genegeerd werden en markers over de HELE box mapten.
  const meshConfigRef = useRef<MeshConfig | null>(null);
  const [logoTexture, setLogoTexture] = useState<THREE.Texture | null>(null);
  const [logoScale, setLogoScale] = useState<{ scaleX: number; scaleY: number }>({ scaleX: 1.0, scaleY: 1.0 });
  // Heeft de modal ooit een schaal doorgegeven? Zo ja, dan is die leidend voor de
  // mesh-afmeting en mag de aspect-fit hieronder niet nog eens meegerekend worden.
  const hasModalScaleRef = useRef(false);
  // Logo position offset from centroid (0.5 = centered, 0 = left/top, 1 = right/bottom)
  const [logoOffset, setLogoOffset] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const logoMeshRef = useRef<THREE.Mesh | null>(null);
  // "Toon alles": extra decals voor de overige gekozen posities
  const extraLogoMeshesRef = useRef<THREE.Mesh[]>([]);
  const [extraPlacements, setExtraPlacements] = useState<{ techniqueId: string; sizePct: number; logoDataUrl?: string }[]>([]);
  const extraTextureCacheRef = useRef<Map<string, THREE.Texture>>(new Map());
  // Handtekening van de laatste hoofd-decal-build (skip identieke rebuilds)
  const lastBuildSigRef = useRef('');
  // Handtekening van de laatste extras-build (skip herbouw bij bv. kleurwissel)
  const lastExtrasSigRef = useRef('');
  // Handtekening van het laatste verwerkte update-bericht (dedupe van retry-sends)
  const lastMsgSigRef = useRef('');
  // Handtekening van de logo-INHOUD (zonder sku): pure kleurwissels slaan het
  // texture/decal-werk volledig over
  const lastContentSigRef = useRef('');
  const [mainMeshVersion, setMainMeshVersion] = useState(0);
  const [centroidsData, setCentroidsData] = useState<Centroid[]>([]);
  // Ruim genoeg om niet met het oppervlak te vechten; per product kan het in de
  // database anders staan en dat wint.
  const [surfaceOffset, setSurfaceOffset] = useState<number>(0.0005);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const glbInputRef = useRef<HTMLInputElement>(null);
  const [uploadingGlb, setUploadingGlb] = useState(false);
  const [productsVersion, setProductsVersion] = useState(0);

  // URL params
  const urlParams = new URLSearchParams(window.location.search);
  const skuFromUrl = urlParams.get('sku');
  const isEmbedMode = urlParams.get('embed') === 'true' || (typeof window !== 'undefined' && window.self !== window.top);
  const techniqueFromUrl = urlParams.get('technique');
  const positionFromUrl = urlParams.get('position'); // zelfde techniekcode op meerdere vlakken (PF)
  const logoFromUrl = urlParams.get('logo');
  const autoScreenshot = urlParams.get('autoScreenshot') === 'true';
  // ?cap=1 -> toon de open/dicht-knop ook in embed (voor de full-screen presentatie)
  const showCapInEmbed = urlParams.get('cap') === '1';
  // ?hgwait=1 -> er komt een HG-wrap aan: laat het product pas zien als die er is
  // (anders zie je eerst de onbedrukte beker).
  const hgWait = urlParams.get('hgwait') === '1';

  // Check localStorage for logo passed from magic_modal
  const [pendingLogo, setPendingLogo] = useState<string | null>(null);
  const [pendingTechnique, setPendingTechnique] = useState<string | null>(null);

  useEffect(() => {
    // EMBED: de modal stuurt logo + EXACTE positie via postMessage. De localStorage/
    // URL-route kent alleen de techniek-CODE (code-only seeding) waardoor het logo
    // eerst op de verkeerde positie (eerste rij = voorkant) verscheen. Overslaan dus.
    if (isEmbedMode) {
      localStorage.removeItem('magic_logo_preview');
      localStorage.removeItem('magic_logo_technique');
      // Headless screenshot (puppeteer, ?logo=&autoScreenshot=true): techniek (+positie) komt wél via de URL
      if (techniqueFromUrl && (logoFromUrl || autoScreenshot)) setPendingTechnique(techniqueFromUrl);
      return;
    }
    // Read logo from localStorage (set by magic_modal) - legacy fallback for same-domain
    const storedLogo = localStorage.getItem('magic_logo_preview');
    const storedTechnique = localStorage.getItem('magic_logo_technique');

    if (storedLogo) {
      console.log('Found logo in localStorage, will apply after model loads');
      setPendingLogo(storedLogo);
      // Clear from localStorage after reading (one-time transfer)
      localStorage.removeItem('magic_logo_preview');
    }

    if (storedTechnique) {
      console.log('Found technique in localStorage:', storedTechnique);
      setPendingTechnique(storedTechnique);
      localStorage.removeItem('magic_logo_technique');
    } else if (techniqueFromUrl) {
      // Fall back to URL param if no localStorage
      setPendingTechnique(techniqueFromUrl);
    }
  }, [techniqueFromUrl]);

  // Load logo from URL parameter (for headless screenshots via Puppeteer)
  useEffect(() => {
    if (logoFromUrl && !pendingLogo && !logoTexture) {
      console.log('[3D] Loading logo from URL:', logoFromUrl);

      // Fetch the logo and convert to data URL
      fetch(logoFromUrl)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            console.log('[3D] Logo loaded from URL, setting as pending');
            setPendingLogo(dataUrl);
          };
          reader.readAsDataURL(blob);
        })
        .catch(error => {
          console.error('[3D] Failed to load logo from URL:', error);
        });
    }
  }, [logoFromUrl, pendingLogo, logoTexture]);

  // Listen for postMessage from parent (magic_modal) - works cross-domain
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Accept messages from any origin for now (modal and app domains)
      const { type, logoDataUrl, technique, sku, logoPosition } = event.data || {};

      // Open/dicht-variant: beide GLB's zijn geladen, dus direct wisselen.
      // Loopt de preload nog, dan wisselen we zodra hij klaar is.
      if (type === 'toggle_open_glb') {
        const wilOpen = !!(event.data && event.data.open);
        const wissel = () => {
          if (!openModelRef.current) return;
          if (modelRef.current) modelRef.current.visible = !wilOpen;
          openModelRef.current.visible = wilOpen;
          if (wilOpen && openMixerRef.current) {
            openActionsRef.current.forEach((actie) => { actie.reset(); actie.play(); });
            clockRef.current.getDelta();
            mixerRef.current = openMixerRef.current;
          } else {
            mixerRef.current = null;
          }
        };
        if (openModelRef.current) { wissel(); }
        else if (openLoadPromiseRef.current) { openLoadPromiseRef.current.then(wissel); }
        return;
      }

      // HG: samengestelde laag-textuur rondom het product wrappen
      if (type === 'hg_wrap') {
        const d: any = event.data || {};
        applyHgWrapRef.current?.(d.dataUrl || '', d.printWidthMm, d.printHeightMm, d.productHeightMm, d.bottomOffsetMm, d.heightUrl, d.reliefMaxMm, d.view, d.seamPct);
        // Ook een LEGE wrap (nog geen lagen) betekent dat de drawer klaar is, dus
        // mag het product zichtbaar worden. Zonder dit bleef het model tot de
        // noodrem verborgen en leek het laden ~10 seconden te duren.
        if (!d.dataUrl && modelRef.current && !modelRef.current.visible) {
          modelRef.current.visible = true;
          setLoading(false);
        }
        return;
      }

      // Handle screenshot request from parent
      if (type === 'request_screenshot') {
        console.log('[3D] Screenshot requested');
        try {
          if (rendererRef.current && sceneRef.current && cameraRef.current) {
            // Render one frame to ensure everything is up-to-date
            rendererRef.current.render(sceneRef.current, cameraRef.current);

            // Get the canvas data URL
            const dataUrl = rendererRef.current.domElement.toDataURL('image/png');

            // Send back to parent
            window.parent.postMessage({
              type: 'screenshot_response',
              dataUrl: dataUrl,
            }, '*');
            console.log('[3D] Screenshot sent to parent');
          } else {
            window.parent.postMessage({
              type: 'screenshot_error',
              error: 'Renderer not ready',
            }, '*');
          }
        } catch (error) {
          console.error('[3D] Screenshot error:', error);
          window.parent.postMessage({
            type: 'screenshot_error',
            error: String(error),
          }, '*');
        }
        return;
      }

      // Vier aanzichten voor "Create artwork". De camera draait per kwartslag om het
      // middelpunt van de controls; hoogte en afstand blijven gelijk, zodat de vier
      // beelden onderling vergelijkbaar zijn. Daarna wordt de oorspronkelijke stand
      // hersteld, dus de gebruiker merkt er niets van.
      if (type === 'request_views') {
        try {
          const rend = rendererRef.current;
          const sc = sceneRef.current;
          const cam = cameraRef.current;
          const ctr = controlsRef.current;
          if (!rend || !sc || !cam || !ctr) {
            window.parent.postMessage({ type: 'views_error', error: 'Renderer not ready' }, '*');
            return;
          }
          const bewaard = cam.position.clone();
          const doel = ctr.target.clone();
          const straal = Math.hypot(bewaard.x - doel.x, bewaard.z - doel.z);
          const hoogte = bewaard.y;
          const starthoek = Math.atan2(bewaard.x - doel.x, bewaard.z - doel.z);
          const namen = ['front', 'right', 'back', 'left'];
          const views: { view: string; dataUrl: string }[] = [];
          for (let i = 0; i < 4; i++) {
            const hoek = starthoek + (i * Math.PI) / 2;
            cam.position.set(doel.x + Math.sin(hoek) * straal, hoogte, doel.z + Math.cos(hoek) * straal);
            cam.lookAt(doel);
            ctr.update();
            rend.render(sc, cam);
            views.push({ view: namen[i], dataUrl: rend.domElement.toDataURL('image/png') });
          }
          // stand terugzetten
          cam.position.copy(bewaard);
          cam.lookAt(doel);
          ctr.update();
          rend.render(sc, cam);
          window.parent.postMessage({ type: 'views_response', views }, '*');
          console.log('[3D] Vier aanzichten verstuurd');
        } catch (error) {
          console.error('[3D] Views error:', error);
          window.parent.postMessage({ type: 'views_error', error: String(error) }, '*');
        }
        return;
      }

      // Handle logo-only screenshot request (curved logo without product, default view)
      if (type === 'request_logo_only') {
        console.log('[3D] Logo-only screenshot requested');
        try {
          if (rendererRef.current && sceneRef.current && cameraRef.current && logoMeshRef.current && modelRef.current && controlsRef.current) {
            // Store current state - we'll restore after screenshot
            const savedSize = rendererRef.current.getSize(new THREE.Vector2());

            // Save current camera position and controls target (user may have rotated)
            const savedCameraPosition = cameraRef.current.position.clone();
            const savedControlsTarget = controlsRef.current.target.clone();

            // Calculate DEFAULT camera position (same logic as when model loads)
            // This ensures screenshot matches the default 3D view, not user's rotated view
            const camBox = new THREE.Box3().setFromObject(modelRef.current);
            const camSize = camBox.getSize(new THREE.Vector3());
            const camCenter = camBox.getCenter(new THREE.Vector3());
            const camMaxDim = Math.max(camSize.x, camSize.y, camSize.z);
            const cameraDistance = camMaxDim * 1.5;

            // Position camera at 90 degrees (kwartslag naar rechts) - same as default view
            // Lower Y = less tilt (more frontal view)
            const angle = 90 * (Math.PI / 180);
            const camX = Math.sin(angle) * cameraDistance;
            const camZ = Math.cos(angle) * cameraDistance;
            cameraRef.current.position.set(camX, cameraDistance * 0.25, camZ);
            cameraRef.current.lookAt(camCenter);
            controlsRef.current.target.copy(camCenter);
            controlsRef.current.update();

            console.log(`[3D] Camera reset to default position: (${camX.toFixed(2)}, ${(cameraDistance * 0.25).toFixed(2)}, ${camZ.toFixed(2)})`);

            // Hide the product model, keep the curved logo mesh visible
            modelRef.current.visible = false;

            // Use current renderer size for consistency with what user sees
            const canvasWidth = savedSize.x;
            const canvasHeight = savedSize.y;

            // Render with transparent background
            const originalBackground = sceneRef.current.background;
            sceneRef.current.background = null;
            rendererRef.current.setClearColor(0x000000, 0);
            rendererRef.current.render(sceneRef.current, cameraRef.current);

            // Get the logo mesh bounding box in screen coordinates
            const logoBox = new THREE.Box3().setFromObject(logoMeshRef.current);
            const logoCorners = [
              new THREE.Vector3(logoBox.min.x, logoBox.min.y, logoBox.min.z),
              new THREE.Vector3(logoBox.min.x, logoBox.min.y, logoBox.max.z),
              new THREE.Vector3(logoBox.min.x, logoBox.max.y, logoBox.min.z),
              new THREE.Vector3(logoBox.min.x, logoBox.max.y, logoBox.max.z),
              new THREE.Vector3(logoBox.max.x, logoBox.min.y, logoBox.min.z),
              new THREE.Vector3(logoBox.max.x, logoBox.min.y, logoBox.max.z),
              new THREE.Vector3(logoBox.max.x, logoBox.max.y, logoBox.min.z),
              new THREE.Vector3(logoBox.max.x, logoBox.max.y, logoBox.max.z),
            ];

            // Project corners to screen space
            let minScreenX = Infinity, minScreenY = Infinity;
            let maxScreenX = -Infinity, maxScreenY = -Infinity;
            for (const corner of logoCorners) {
              const projected = corner.clone().project(cameraRef.current);
              const screenX = (projected.x + 1) / 2 * canvasWidth;
              const screenY = (1 - projected.y) / 2 * canvasHeight; // Y is inverted
              minScreenX = Math.min(minScreenX, screenX);
              minScreenY = Math.min(minScreenY, screenY);
              maxScreenX = Math.max(maxScreenX, screenX);
              maxScreenY = Math.max(maxScreenY, screenY);
            }

            // Add padding and clamp to canvas bounds
            const padding = 10;
            minScreenX = Math.max(0, Math.floor(minScreenX - padding));
            minScreenY = Math.max(0, Math.floor(minScreenY - padding));
            maxScreenX = Math.min(canvasWidth, Math.ceil(maxScreenX + padding));
            maxScreenY = Math.min(canvasHeight, Math.ceil(maxScreenY + padding));

            const cropWidth = maxScreenX - minScreenX;
            const cropHeight = maxScreenY - minScreenY;

            console.log(`[3D] Logo bounds: (${minScreenX}, ${minScreenY}) to (${maxScreenX}, ${maxScreenY}), size: ${cropWidth}x${cropHeight}`);

            // Create cropped canvas
            const croppedCanvas = document.createElement('canvas');
            croppedCanvas.width = cropWidth;
            croppedCanvas.height = cropHeight;
            const croppedCtx = croppedCanvas.getContext('2d');
            if (croppedCtx) {
              croppedCtx.drawImage(
                rendererRef.current.domElement,
                minScreenX, minScreenY, cropWidth, cropHeight,
                0, 0, cropWidth, cropHeight
              );
            }

            const dataUrl = croppedCanvas.toDataURL('image/png');
            console.log(`[3D] Logo-only cropped to ${cropWidth}x${cropHeight}`);

            // Restore everything - model visibility
            modelRef.current.visible = true;
            sceneRef.current.background = originalBackground;
            rendererRef.current.setClearColor(0x1a1a2e, 1);

            // Restore camera to user's position (so they don't see the view jump)
            cameraRef.current.position.copy(savedCameraPosition);
            controlsRef.current.target.copy(savedControlsTarget);
            controlsRef.current.update();
            rendererRef.current.render(sceneRef.current, cameraRef.current);

            // Send back to parent with actual cropped dimensions
            window.parent.postMessage({
              type: 'logo_only_response',
              dataUrl: dataUrl,
              logoWidth: cropWidth,
              logoHeight: cropHeight,
            }, '*');
            console.log(`[3D] Logo-only screenshot sent: ${cropWidth}x${cropHeight}`);
          } else {
            window.parent.postMessage({
              type: 'logo_only_error',
              error: 'Renderer or logo mesh not ready',
            }, '*');
          }
        } catch (error) {
          console.error('[3D] Logo-only screenshot error:', error);
          window.parent.postMessage({
            type: 'logo_only_error',
            error: String(error),
          }, '*');
        }
        return;
      }

      if (type === 'magic_logo_data') {
        // Initial logo load
        if (logoDataUrl) {
          setPendingLogo(logoDataUrl);
        }
        if (technique) {
          // Positie erbij? Dan de exacte rij (code+positie) selecteren i.p.v. alleen code
          const pos = (event.data || {}).techniquePosition;
          const row = pos ? techniques.find(t => t.technique_code === technique && String(t.position).toLowerCase() === String(pos).toLowerCase()) : null;
          setPendingTechnique(row ? String(row.id) : technique);
        }
      } else if (type === 'magic_logo_update') {
        // PERFORMANCE: de modal stuurt hetzelfde bericht meermaals (retry-timers).
        // Identieke payload opnieuw verwerken kost seconden aan decal-rebuilds op
        // grote meshes - dus alleen echt gewijzigde updates verwerken.
        const _d = event.data || {};
        const _lp = (s: string | null | undefined) => s ? `${s.length}:${s.slice(0, 32)}:${s.slice(-16)}` : '';
        const plcSig = Array.isArray(_d.placements)
          ? (_d.placements as any[]).map((p: any) => `${p.technique}|${p.techniquePosition}|${p.sizePct}|${_lp(p.logoDataUrl)}`).join(';')
          : '';
        const msgSig = [sku, technique, _d.techniquePosition, _lp(logoDataUrl), JSON.stringify(logoPosition || null), plcSig].join('#');
        // Pas dedupen zodra de viewer klaar is (technieken + model geladen);
        // eerdere berichten mogen herhalen totdat ze goed verwerkt konden worden.
        if (techniques.length > 0 && !loading) {
          if (msgSig === lastMsgSigRef.current) {
            // zelfde staat als al getoond -> loader kan weg
            try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
            return;
          }
          lastMsgSigRef.current = msgSig;
          // echte wijziging geaccepteerd: loader aan tot decals/model er staan
          try { window.parent.postMessage({ type: 'magic_3d_loading' }, '*'); } catch (e) { /* geen parent */ }
        }
        // Berichten die binnenkomen terwijl het model nog laadt posten NIETS:
        // het load-effect meldt zelf loading/ready, anders blijft de loader
        // hangen tot de failsafe (kleurwissel voelde daardoor traag).

        // Check if SKU changed - need to reload model for different color variant
        if (sku && sku !== selectedSku) {
          console.log('SKU changed via postMessage:', sku);
          setSelectedSku(sku);
        }

        // Pure kleurwissel (alleen sku anders, logo en posities identiek):
        // decals zijn losse scene-objecten en blijven gewoon staan. Geen
        // texture-reload of decal-rebuild nodig; het load-effect meldt
        // loading/ready voor het model zelf.
        const inhoudSig = [technique, _d.techniquePosition, _lp(logoDataUrl), JSON.stringify(logoPosition || null), plcSig].join('#');
        // ook tijdens model-laden geldig: de decal blijft staan bij gelijke inhoud
        if (techniques.length > 0 && logoMeshRef.current && inhoudSig === lastContentSigRef.current) {
          return;
        }
        lastContentSigRef.current = inhoudSig;

        // Real-time logo update - update texture directly for performance
        if (logoDataUrl && logoMeshRef.current) {
          // Update existing texture directly for smooth real-time updates
          const textureLoader = new THREE.TextureLoader();
          textureLoader.load(logoDataUrl, (newTexture) => {
            // Copy settings from existing texture
            newTexture.minFilter = THREE.LinearMipmapLinearFilter;
            newTexture.magFilter = THREE.LinearFilter;
            newTexture.generateMipmaps = true;
            newTexture.wrapS = THREE.ClampToEdgeWrapping;
            newTexture.wrapT = THREE.ClampToEdgeWrapping;
            const maxAnisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
            newTexture.anisotropy = maxAnisotropy;
            newTexture.premultiplyAlpha = false;
            newTexture.colorSpace = THREE.SRGBColorSpace;

            // Copy rotation from existing texture if any
            if (logoTexture) {
              newTexture.center.copy(logoTexture.center);
              newTexture.rotation = logoTexture.rotation;
            }
            newTexture.needsUpdate = true;

            // Update the material's map directly
            const material = logoMeshRef.current?.material as THREE.MeshStandardMaterial;
            if (material && material.map) {
              material.map.dispose(); // Clean up old texture
              material.map = newTexture;
              material.needsUpdate = true;
            }

            // Update state
            setLogoTexture(newTexture);
          });
        } else if (logoDataUrl) {
          // No mesh yet, use pending logo approach
          setPendingLogo(logoDataUrl);
        }
        if (technique) {
          const pos = (event.data || {}).techniquePosition;
          const row = pos ? techniques.find(t => t.technique_code === technique && String(t.position).toLowerCase() === String(pos).toLowerCase()) : null;
          const key = row ? String(row.id) : technique;
          if (key !== selectedTechnique) setSelectedTechnique(key);
        }
        // Extra posities ("Toon alles"): logo op alle gekozen posities tegelijk
        const plc = (event.data || {}).placements;
        if (Array.isArray(plc)) {
          const resolved = plc.map((p: any) => {
            const row = p.techniquePosition
              ? techniques.find(t => t.technique_code === p.technique && String(t.position).toLowerCase() === String(p.techniquePosition).toLowerCase())
              : techniques.find(t => t.technique_code === p.technique);
            return row ? { techniqueId: String(row.id), sizePct: Number(p.sizePct) || 1, logoDataUrl: typeof p.logoDataUrl === 'string' ? p.logoDataUrl : undefined } : null;
          }).filter(Boolean) as { techniqueId: string; sizePct: number; logoDataUrl?: string }[];
          setExtraPlacements(prev => JSON.stringify(prev) === JSON.stringify(resolved) ? prev : resolved);
        }

        // Update logo scale and position from modal's position data
        if (logoPosition) {
          if (logoPosition.scaleX !== undefined || logoPosition.scaleY !== undefined) {
            const newScaleX = logoPosition.scaleX ?? 1.0;
            const newScaleY = logoPosition.scaleY ?? 1.0;
            console.log('[3D] Logo scale from modal:', { scaleX: newScaleX, scaleY: newScaleY });
            hasModalScaleRef.current = true;
            setLogoScale({ scaleX: newScaleX, scaleY: newScaleY });
          }
          // Update logo position offset (0-1 range, 0.5 = centered)
          if (logoPosition.x !== undefined || logoPosition.y !== undefined) {
            const newX = logoPosition.x ?? 0.5;
            const newY = logoPosition.y ?? 0.5;
            console.log('[3D] Logo position from modal:', { x: newX, y: newY });
            setLogoOffset({ x: newX, y: newY });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [logoTexture, selectedTechnique, selectedSku, products, techniques]);

  // Initialize Three.js
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    // Wit als achtergrond: het 3D-beeld staat in een wit scherm en gaat als
    // opname mee naar de proefdruk. Een grijs vlak eromheen valt daar op als
    // een vlek die er niet hoort.
    scene.background = new THREE.Color(0xffffff);
    sceneRef.current = scene;

    // Camera - use smaller FOV for less distortion and start further away
    const camera = new THREE.PerspectiveCamera(
      45, // Smaller FOV = less fisheye, product looks better
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.001,
      1000
    );
    // Initial camera position
    camera.position.set(1.75, 1.05, 1.75);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create neutral studio environment for metallic reflections
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    // Create a neutral gradient environment (studio-like)
    const envScene = new THREE.Scene();

    // Create gradient sphere for soft reflections - bright top for metallic, dark bottom for matte
    const envGeo = new THREE.SphereGeometry(50, 32, 32);
    const envMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      uniforms: {
        topColor: { value: new THREE.Color(0xdddddd) },     // Bright gray top (for metallic reflections)
        bottomColor: { value: new THREE.Color(0x444444) },  // Dark bottom
        offset: { value: 0 },
        exponent: { value: 0.5 }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `
    });
    const envMesh = new THREE.Mesh(envGeo, envMat);
    envScene.add(envMesh);

    // Generate environment map from the gradient scene
    const envMap = pmremGenerator.fromScene(envScene, 0.04).texture;
    scene.environment = envMap;

    // Cleanup temp scene
    envGeo.dispose();
    envMat.dispose();
    pmremGenerator.dispose();

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, 0.05, 0);
    // Invert zoom direction so scroll-up brings product closer (more intuitive)
    controls.zoomSpeed = -1.0;
    controlsRef.current = controls;

    // Lights - balanced from all sides with shadow bias fix
    scene.add(new THREE.AmbientLight(0xffffff, 1.2)); // Even stronger ambient to reduce shadow contrast

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
    dirLight.position.set(5, 5, 5);
    dirLight.castShadow = true;
    // Shadow bias to prevent shadow acne/banding artifacts
    dirLight.shadow.bias = -0.001;
    dirLight.shadow.normalBias = 0.02;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    scene.add(dirLight);

    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
    dirLight2.position.set(-5, 5, -5);
    scene.add(dirLight2);

    // Extra fill lights for even illumination from all sides
    const dirLight3 = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight3.position.set(0, -5, 5);
    scene.add(dirLight3);

    const dirLight4 = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight4.position.set(0, 5, -5);
    scene.add(dirLight4);

    // Additional side lights to eliminate any remaining shadows
    const dirLight5 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight5.position.set(5, 0, -5);
    scene.add(dirLight5);

    const dirLight6 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight6.position.set(-5, 0, 5);
    scene.add(dirLight6);

    // Ground plane removed - clean floating product view

    // Loader
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(import.meta.env.BASE_URL + 'draco/'); // lokaal (public/draco), geen externe CDN-wachttijd
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    loaderRef.current = loader;

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      if (mixerRef.current) {
        mixerRef.current.update(clockRef.current.getDelta());
        // Stop de cap-animatie exact op het doelframe (open of dicht)
        const target = capTargetRef.current;
        const actie = capActionRef.current;
        if (target !== null && actie) {
          const reached = actie.timeScale >= 0 ? actie.time >= target : actie.time <= target;
          if (reached) {
            actie.time = target;
            actie.paused = true;
            capTargetRef.current = null;
            mixerRef.current.update(0);
            setCapBusy(false);
          }
        }
      }
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      containerRef.current?.removeChild(renderer.domElement);
    };
  }, []);

  // Fetch products
  useEffect(() => {
    fetch('/api/3d/products' + (skuFromUrl ? '?sku=' + encodeURIComponent(skuFromUrl) : ''))
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setProducts(data.data || []);

          // Auto-load from URL if sku provided
          if (skuFromUrl) {
            const product = data.data.find((p: Product3D) =>
              p.variant_sku === skuFromUrl || p.base_sku === skuFromUrl
            );
            if (product) {
              // alleen selecteren: het selectedSku-effect laadt het model
              // (directe load hier gaf een DUBBELE model-load bij openen)
              setSelectedSku(product.variant_sku);
            }
          }
        }
      })
      .catch(err => console.error('Error loading products:', err));
  }, [skuFromUrl, productsVersion]);

  // Load product with config and centroids
  // sku-wissel via postMessage (kleurwissel in de modal): laad het bijbehorende
  // model zodra selectedSku verandert en het nog niet het geladen model is.
  const loadedSkuRef = useRef<string>('');
  useEffect(() => {
    if (!selectedSku || products.length === 0) return;
    if (loadedSkuRef.current === selectedSku) return;
    const product = products.find(p => p.variant_sku === selectedSku)
      || products.find(p => p.base_sku === selectedSku);
    if (product) {
      loadedSkuRef.current = selectedSku;
      loadProduct(product);
      try {
        window.parent.postMessage({ type: 'open_variant_available', available: !!product.open_glb_path }, '*');
      } catch (e) { /* geen parent */ }
    } else {
      // variant (nog) niet in de lijst (gefilterde lijst + kleurwissel): gericht ophalen
      fetch('/api/3d/products?sku=' + encodeURIComponent(selectedSku))
        .then(r => r.json())
        .then(d => {
          if (d && d.success && Array.isArray(d.data) && d.data.length > 0) {
            setProducts(prev => [...prev, ...d.data.filter((n: Product3D) => !prev.some(p => p.variant_sku === n.variant_sku))]);
          }
        })
        .catch(() => { /* geen 3D voor deze variant */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSku, products]);

  // Open-variant (onzichtbaar) in de scene voorbereiden; resolve't als hij klaar is
  const preloadOpenVariant = useCallback(async (product: Product3D) => {
    if (!product.open_glb_path || !loaderRef.current) return;
    const openPad = '/models/' + String(product.open_glb_path).replace(/^\/?(models\/)?/, '');
    const maxAnisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
    const [gltfOpen, config] = await Promise.all([
      loaderRef.current.loadAsync(openPad),
      loadConfig(product.variant_sku),
    ]);
    const openModel = gltfOpen.scene;
    openModel.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((m) => {
          const mat = m as THREE.MeshStandardMaterial;
          if (!mat) return;
          mat.flatShading = false;
          if (mat.normalMap) mat.normalScale.set(0.5, 0.5);
          if (mat.map) { mat.map.anisotropy = maxAnisotropy; mat.map.colorSpace = THREE.SRGBColorSpace; mat.map.needsUpdate = true; }
          mat.needsUpdate = true;
        });
      }
    });
    // De open-GLB bevat twee tassen: grp_dicht (de dichtstand, die tonen we al
    // via de aparte dichte GLB) en grp_open. Statische schalen van 0 naar 1
    // zetten (onderdelen staan verstopt) en daarna de dichte groep verbergen.
    openModel.traverse((o) => {
      if (o.scale.x === 0 && o.scale.y === 0 && o.scale.z === 0) o.scale.set(1, 1, 1);
    });
    const grpDicht = openModel.getObjectByName('grp_dicht');
    if (grpDicht) grpDicht.scale.set(0, 0, 0);

    // Eerst de animatie opzetten en het model in de begin-pose (dicht) zetten:
    // de rustpose van de export staat al open, en daarop uitlijnen laat de tas verschuiven
    let openMixer: THREE.AnimationMixer | null = null;
    if (gltfOpen.animations && gltfOpen.animations.length > 0) {
      openMixer = new THREE.AnimationMixer(openModel);
      openActionsRef.current = gltfOpen.animations.map((clip) => {
        // Alleen de echte beweging (klep die openzwaait, props): rotatie- en
        // translatie-tracks. De weg-schaal/schuif-crossfade van grp_dicht en
        // het in-schalen van grp_open zorgen juist voor het verschuiven.
        const beperkt = clip.clone();
        beperkt.tracks = beperkt.tracks.filter((tr) => {
          const naam = tr.name.split('.')[0];
          if (naam === 'grp_dicht' || naam.indexOf('_dicht') >= 0) return false;
          return tr.name.endsWith('.quaternion') || tr.name.endsWith('.position');
        });
        const actie = openMixer!.clipAction(beperkt);
        actie.setLoop(THREE.LoopOnce, 1);
        actie.clampWhenFinished = true;
        actie.play();
        actie.paused = true;
        return actie;
      }).filter((a) => a.getClip().tracks.length > 0);
      openMixer.update(0);
    }

    const referentie = modelRef.current;
    const openBox = new THREE.Box3().setFromObject(openModel);
    const openSize = openBox.getSize(new THREE.Vector3());
    if (referentie) {
      // IJken op de ingebedde dichte tas (grp_dicht): exact dezelfde geometrie
      // als het losse dichte model, dus een 1-op-1 match zonder klep/props-ruis
      const dichtBox = new THREE.Box3().setFromObject(referentie);
      const dichtSize = dichtBox.getSize(new THREE.Vector3());
      const dichtCenter = dichtBox.getCenter(new THREE.Vector3());
      const anker = openModel.getObjectByName('grp_dicht') || openModel;
      const ankerSchaalOud = anker.scale.clone();
      anker.scale.set(1, 1, 1);
      openModel.updateMatrixWorld(true);
      const ankerBox = new THREE.Box3().setFromObject(anker);
      const ankerSize = ankerBox.getSize(new THREE.Vector3());
      const fac = ankerSize.x > 0 ? dichtSize.x / ankerSize.x : 1;
      anker.scale.copy(ankerSchaalOud); // weer verbergen
      openModel.scale.setScalar(fac);
      openModel.updateMatrixWorld(true);
      // ankerBox schaalt mee vanaf de oorsprong (openModel staat nog op 0)
      openModel.position.x += dichtCenter.x - fac * (ankerBox.min.x + ankerBox.max.x) / 2;
      openModel.position.y += dichtBox.max.y - fac * ankerBox.max.y;
      openModel.position.z += dichtCenter.z - fac * (ankerBox.min.z + ankerBox.max.z) / 2;
      console.log('open-model geijkt op grp_dicht, factor', fac.toFixed(3));
    } else {
      const openMax = Math.max(openSize.x, openSize.y, openSize.z);
      openModel.scale.setScalar((1.0 / openMax) * (config?.scale || 1.0));
      openBox.setFromObject(openModel);
      const openCenter = openBox.getCenter(new THREE.Vector3());
      openModel.position.set(-openCenter.x, -openBox.min.y, -openCenter.z);
    }
    openModel.visible = false;
    if (sceneRef.current) sceneRef.current.add(openModel);
    openModelRef.current = openModel;
    openMixerRef.current = openMixer;
    console.log('Open-variant voorgeladen:', openPad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProduct = useCallback(async (product: Product3D, behoudTransform = false) => {
    if (!loaderRef.current || !sceneRef.current) return;

    setLoading(true);
    setStatus('Model laden...');

    // Remove old model
    if (modelRef.current) {
      sceneRef.current.remove(modelRef.current);
      modelRef.current.traverse((child) => {
        if ((child as THREE.Mesh).geometry) (child as THREE.Mesh).geometry.dispose();
        if ((child as THREE.Mesh).material) {
          const mat = (child as THREE.Mesh).material;
          if (Array.isArray(mat)) mat.forEach(m => m.dispose());
          else mat.dispose();
        }
      });
    }

    mixerRef.current = null;
    capActionRef.current = null;
    capTargetRef.current = null;
    setHasCap(false);
    setCapOpen(false);
    setCapBusy(false);

    // Voorgeladen open-variant opruimen
    if (openModelRef.current && sceneRef.current) {
      sceneRef.current.remove(openModelRef.current);
      openModelRef.current.traverse((child) => {
        if ((child as THREE.Mesh).geometry) (child as THREE.Mesh).geometry.dispose();
        if ((child as THREE.Mesh).material) {
          const mat = (child as THREE.Mesh).material;
          if (Array.isArray(mat)) mat.forEach(m => m.dispose());
          else mat.dispose();
        }
      });
    }
    openModelRef.current = null;
    openMixerRef.current = null;
    openActionsRef.current = [];
    openLoadPromiseRef.current = null;

    // Remove old technique mesh
    if (techniqueMeshRef.current) {
      sceneRef.current.remove(techniqueMeshRef.current);
      techniqueMeshRef.current.geometry.dispose();
      (techniqueMeshRef.current.material as THREE.Material).dispose();
      techniqueMeshRef.current = null;
    }

    // Remove old centroid markers
    removeCentroidMarkers();

    try {
      console.log('=== loadProduct START ===', product.variant_sku);

      // Load GLB model
      let glbPath = product.glb_path;
      if (!glbPath.startsWith('http') && !glbPath.startsWith('/')) {
        glbPath = glbPath.replace(/^models\//, '');
        glbPath = `/models/${glbPath}`;
      }
      console.log('Loading model from:', glbPath);

      const gltf = await loaderRef.current.loadAsync(glbPath);
      const model = gltf.scene;

      // Get max anisotropy for texture quality
      const maxAnisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;

      // Process model meshes - enable shadows, compute normals, improve texture quality
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          mesh.castShadow = true;
          mesh.receiveShadow = true;

          // Aggressive vertex welding and normal smoothing to fix banding/seams
          if (mesh.geometry) {
            // First convert to non-indexed geometry so all vertices can be accessed
            const nonIndexedGeometry = mesh.geometry.toNonIndexed();
            // Then merge vertices within tolerance - higher tolerance = more aggressive welding
            mesh.geometry = BufferGeometryUtils.mergeVertices(nonIndexedGeometry, 0.001);
            // Recompute smooth normals
            mesh.geometry.computeVertexNormals();
            // Cleanup
            nonIndexedGeometry.dispose();
          }

          // Improve texture quality on materials
          const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          materials.forEach((mat, idx) => {
            // Log material properties for debugging
            console.log(`=== MATERIAL ${idx}: ${mat.name || 'unnamed'} ===`);
            console.log(`  Type: ${mat.type}`);
            if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhysicalMaterial) {
              console.log(`  Color: #${mat.color.getHexString()}`);
              console.log(`  Metalness (original): ${mat.metalness}`);
              console.log(`  Roughness (original): ${mat.roughness}`);
              console.log(`  Has map: ${!!mat.map}`);
              console.log(`  Has normalMap: ${!!mat.normalMap}`);
              console.log(`  Has metalnessMap: ${!!mat.metalnessMap}`);
              console.log(`  Has roughnessMap: ${!!mat.roughnessMap}`);

              // Force smooth shading and reduce roughness to minimize banding visibility
              mat.flatShading = false;
              // Reduce normal map intensity if present (can cause visible seams)
              if (mat.normalMap) {
                mat.normalScale.set(0.5, 0.5);
              }
              mat.needsUpdate = true;
              // Improve color/albedo texture (sRGB color space)
              if (mat.map) {
                mat.map.minFilter = THREE.LinearMipmapLinearFilter;
                mat.map.magFilter = THREE.LinearFilter;
                mat.map.generateMipmaps = true;
                mat.map.anisotropy = maxAnisotropy;
                mat.map.colorSpace = THREE.SRGBColorSpace;
                mat.map.needsUpdate = true;
              }
              // Improve data textures (must stay LINEAR - no color space conversion!)
              [mat.normalMap, mat.roughnessMap, mat.metalnessMap].forEach(tex => {
                if (tex) {
                  tex.minFilter = THREE.LinearMipmapLinearFilter;
                  tex.magFilter = THREE.LinearFilter;
                  tex.generateMipmaps = true;
                  tex.anisotropy = maxAnisotropy;
                  tex.colorSpace = THREE.NoColorSpace; // Data textures must be linear!
                  tex.needsUpdate = true;
                }
              });
            }
          });
        }
      });

      // Get saved config
      const config = await loadConfig(product.variant_sku);
      meshConfigRef.current = config; // synchroon, vóór de centroid-markers
      setMeshConfig(config);

      // De afstand tot het oppervlak staat per product in de database, maar werd
      // hier nooit overgenomen: de viewer bleef op zijn startwaarde staan. Die is
      // zo klein dat de opdruk en het product om dezelfde pixels vechten, en dan
      // krijg je strepen door het logo alsof de dekking niet klopt.
      const uitConfig = parseFloat(String((config as any)?.surface_offset ?? ''));

      if (Number.isFinite(uitConfig) && uitConfig > 0) {
        setSurfaceOffset(uitConfig);
      }

      // ── PF CONCEPT: GLB met per printvlak een "imprint-<areaId>"-mesh. Registreren voor
      //    applyLogoToModel; kleurvariant = eigen textuur (textures/<variant>.png) op de
      //    productmaterialen (het GLB bevat de textuur van één kleur). ──
      pfImprintsRef.current.clear(); pfExtraAreasRef.current.clear();
      if (config && String((config as any).mesh_type) === 'imprint') {
        const pad: any = (config as any).print_area_data || {};
        model.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh) return;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          const mat = mats[0] as THREE.MeshStandardMaterial;
          const m = String(mesh.name || '').match(/^imprint-(.+)$/) || String(mat?.name || '').match(/^imprint-(.+)$/);
          if (!m || !mat) return;
          pfImprintsRef.current.set(m[1], { mesh, material: mat, blank: mat.map || null });
          mat.transparent = true; mat.depthWrite = false;
          mat.polygonOffset = true; mat.polygonOffsetFactor = -2; mat.polygonOffsetUnits = -2;
          mesh.renderOrder = 5;
        });
        console.log('[3D] PF imprint-vlakken: ' + Array.from(pfImprintsRef.current.keys()).join(', '));
        if (pad.texture) {
          const texUrl = '/models/' + String(pad.texture).replace(/^\/?(models\/)?/, '');
          new THREE.TextureLoader().load(texUrl, (tex) => {
            tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = maxAnisotropy;
            tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter; tex.needsUpdate = true;
            model.traverse((child) => {
              const mesh = child as THREE.Mesh; if (!mesh.isMesh) return;
              const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              mats.forEach((mm) => {
                const mat = mm as THREE.MeshStandardMaterial;
                if (!mat || /^imprint-/.test(String(mat.name || '')) || !mat.map) return;
                mat.map = tex; mat.needsUpdate = true;
              });
            });
          }, undefined, () => console.warn('[3D] PF-textuur niet geladen: ' + texUrl));
        }
      }

      // Calculate base scale
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      baseScaleRef.current = 1.0 / maxDim;

      // Apply scale from config
      const finalScale = baseScaleRef.current * (config?.scale || 1.0);
      model.scale.setScalar(finalScale);

      // Center model
      box.setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.set(-center.x, -box.min.y, -center.z);

      // No model rotation - use camera position to view from desired angle
      // Uitzondering XD-kleding (iqoniq T-codes, product_type 'textiel'): die GLB's kijken naar +Z
      // (glTF-conventie) terwijl de viewer-camera op +X staat (Spranz-conventie) -> je zag de zijkant
      // en het logo stond uit beeld. Kwartslag om Y zodat de voorkant naar +X wijst, dan opnieuw centreren.
      if (String(product.glb_path || '').startsWith('xdconnect/') && String((config as any)?.product_type || '') === 'textiel') {
        model.rotation.y = Math.PI / 2;
        model.updateMatrixWorld(true);
        const rbox = new THREE.Box3().setFromObject(model);
        const rc = rbox.getCenter(new THREE.Vector3());
        model.position.x -= rc.x;
        model.position.z -= rc.z;
        model.position.y -= rbox.min.y;
        model.updateMatrixWorld(true);
        console.log('[3D] XD-kleding: model +90 graden om Y (voorkant naar camera)');
      }

      sceneRef.current.add(model);
      modelRef.current = model;

      // Schroefdop-animatie: laad op frame 1 (dicht) en pauzeer. De Open/Sluit-knop
      // speelt naar het juiste frame (open=68). Zonder animatie => geen knop.
      capActionRef.current = null;
      capNodeRef.current = null;
      capTargetRef.current = null;
      setHasCap(false);
      setCapOpen(false);
      setCapBusy(false);
      if (gltf.animations && gltf.animations.length > 0) {
        const mixer = new THREE.AnimationMixer(model);
        const clip = gltf.animations[0];
        const actie = mixer.clipAction(clip);
        actie.setLoop(THREE.LoopOnce, 1);
        actie.clampWhenFinished = true;
        actie.play();
        actie.paused = true;
        actie.time = 0; // frame 1 = dop dicht
        mixer.update(0);
        clockRef.current.getDelta(); // delta resetten
        mixerRef.current = mixer;
        capActionRef.current = actie;
        capClipDurRef.current = clip.duration;
        // Bepaal de dop-node uit de animatietracks (eerste node die echt in
        // het model bestaat), zodat "auf Deckel"-logo's eraan gehangen kunnen
        // worden en meedraaien met Open/Close.
        try {
          const trackNodes = clip.tracks
            .map((t) => THREE.PropertyBinding.parseTrackName(t.name).nodeName)
            .filter((n): n is string => !!n);
          for (const nn of trackNodes) {
            const nodeObj = model.getObjectByName(nn);
            if (nodeObj) { capNodeRef.current = nodeObj; break; }
          }
          if (capNodeRef.current) {
            console.log(`[3D] Dop-node voor animatie: "${capNodeRef.current.name}"`);
          }
        } catch {
          capNodeRef.current = null;
        }
        setHasCap(true);
      } else {
        mixerRef.current = null;
      }

      // Open-variant parallel voorladen; de Open-knop wacht hierop indien nodig
      if (product.open_glb_path) {
        openLoadPromiseRef.current = preloadOpenVariant(product)
          .catch((e) => { console.warn('Open-variant laden mislukt:', e); })
          .finally(() => { openLoadPromiseRef.current = null; });
      }

      if (!behoudTransform) {
      // Reset camera - do it directly here to avoid closure issues
      // Camera distance calculation: larger multiplier = product appears smaller
      const camBox = new THREE.Box3().setFromObject(model);
      const camSize = camBox.getSize(new THREE.Vector3());
      const camCenter = camBox.getCenter(new THREE.Vector3());
      const camMaxDim = Math.max(camSize.x, camSize.y, camSize.z);

      // Camera distance: 1.5 = product appears ~50% smaller than at 0.5
      const cameraDistance = camMaxDim * 1.5;

      console.log('Camera setup: maxDim=', camMaxDim, 'distance=', cameraDistance);

      if (cameraRef.current && controlsRef.current) {
        // Position camera to view print area - kwartslag naar rechts (90 graden)
        // Angle: 90 degrees from front (right side of model)
        // Lower Y = less tilt (more frontal view)
        const angle = 90 * (Math.PI / 180); // 90 degrees = kwartslag naar rechts
        const camX = Math.sin(angle) * cameraDistance;
        const camZ = Math.cos(angle) * cameraDistance;
        cameraRef.current.position.set(camX, cameraDistance * 0.25, camZ);
        cameraRef.current.lookAt(camCenter);
        controlsRef.current.target.copy(camCenter);
        controlsRef.current.update();
      }
      }

      // Load techniques
      const baseSku = product.base_sku || product.variant_sku.split('.')[0];
      await loadTechniques(baseSku);

      // Load centroid markers
      await loadCentroidMarkers(product.variant_sku);

      setStatus('Model geladen');
      // Wacht op de HG-wrap: model verbergen tot de bedrukking er is (max 2,5 s),
      // zodat de gebruiker niet eerst het onbedrukte product ziet.
      if (hgWait && !hgWrapRef.current) {
        model.visible = false;
        setLoading(true);
        window.setTimeout(() => {
          if (modelRef.current && !modelRef.current.visible) { modelRef.current.visible = true; setLoading(false); }
        }, 2500);
      } else {
        setLoading(false);
      }

      // HG-wrap die binnenkwam vóór het model klaar was: nu alsnog toepassen
      if (pendingWrapRef.current) {
        const p = pendingWrapRef.current;
        pendingWrapRef.current = null;
        setTimeout(() => applyHgWrapRef.current?.(p.dataUrl, p.w, p.h, p.ph, p.bo, p.hu, p.rm, p.vw, p.sp), 60);
      }

      // Set ready flag for screenshot service (without logo)
      // If logo is being loaded from URL, __3DLogoApplied will be set later
      if (!logoFromUrl) {
        (window as any).__3DReady = true;
        console.log('[3D] Ready flag set (no logo)');
      }

    } catch (error) {
      console.error('Error loading model:', error);
      setStatus('Fout bij laden model');
      setLoading(false);
    }
  }, []);

  // Load saved config from API
  const loadConfig = async (sku: string): Promise<MeshConfig | null> => {
    try {
      const res = await fetch(`/api/3d/config?sku=${encodeURIComponent(sku)}`);
      const data = await res.json();
      if (data.success && data.data) {
        console.log(`Loaded config for ${sku}: mesh_type=${data.data.mesh_type}, marker_points=${data.data.marker_points?.length || 0}`);
        return data.data;
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
    return null;
  };

  // Reset camera to view model (kept for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _resetCamera = () => {
    console.log('resetCamera check - model:', !!modelRef.current, 'camera:', !!cameraRef.current, 'controls:', !!controlsRef.current);
    if (!modelRef.current || !cameraRef.current || !controlsRef.current) return;

    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Camera distance - 50% further away (zoom out) to make product appear smaller
    // Original was maxDim * 0.5, now maxDim * 1.0 (double distance = 50% smaller)
    const cameraZ = maxDim * 1.0;

    console.log('resetCamera applying - maxDim:', maxDim, 'cameraZ:', cameraZ);

    // Keep original camera angle (diagonal view from corner)
    cameraRef.current.position.set(cameraZ, cameraZ * 0.6, cameraZ);
    cameraRef.current.lookAt(center);
    controlsRef.current.target.copy(center);
    controlsRef.current.update();
  };

  // Load techniques
  const loadTechniques = async (baseSku: string) => {
    try {
      const res = await fetch(`/api/3d/techniques?sku=${encodeURIComponent(baseSku)}`);
      const data = await res.json();
      if (data.success) {
        setTechniques(data.data || []);
        console.log(`Loaded ${data.count} techniques for ${baseSku}`);
      }
    } catch (err) {
      console.error('Error loading techniques:', err);
    }
  };

  // Load centroid markers
  const loadCentroidMarkers = async (sku: string) => {
    try {
      const res = await fetch(`/api/3d/centroids?sku=${encodeURIComponent(sku)}`);
      const data: CentroidResponse = await res.json();

      if (!data.success || !data.centroids?.length) {
        console.log('No centroids found for', sku);
        return;
      }

      console.log(`Loading ${data.count} centroid markers for ${sku}`);

      // Store centroids data for later use (includes productHeight and productDiameter)
      setCentroidsData(data.centroids);

      data.centroids.forEach(centroid => {
        createCentroidMarker(centroid, data.product);
      });

    } catch (err) {
      console.error('Error loading centroids:', err);
    }
  };

  // Create a centroid marker sphere
  const createCentroidMarker = (centroid: Centroid, product: { width: number; height: number; diameter: number }) => {
    if (!sceneRef.current || !modelRef.current) return;

    // Convert 2D centroid position to 3D
    const position = convert2DTo3D(centroid, product);
    if (!position) return;

    const color = POSITION_COLORS[centroid.position.toLowerCase()] || POSITION_COLORS['default'];

    // Create sphere marker
    const markerSize = 0.015;
    const geometry = new THREE.SphereGeometry(markerSize, 32, 32);
    const material = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: 0.8,
      emissive: color,
      emissiveIntensity: 0.3,
      metalness: 0.5,
      roughness: 0.3
    });

    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(position);
    marker.name = `centroid_${centroid.position.toLowerCase()}`;
    marker.visible = false; // Hidden by default

    sceneRef.current.add(marker);
    centroidMarkersRef.current.push(marker);

    console.log(`Created centroid marker for "${centroid.position}" at:`, position);
  };

  // Convert 2D coordinates to 3D position on model
  const convert2DTo3D = (centroid: Centroid, _product: { width: number; height: number; diameter: number }): THREE.Vector3 | null => {
    if (!modelRef.current) return null;

    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());

    // Use yMm directly - it's already in millimeters from top of product
    // Product height in mm
    const productHeightMm = centroid.productHeight || _product.height;

    // yMm = mm vanaf de BOVENkant van het product, exact teruggerekend uit de
    // 2D-pin via de svg_image-bounds (backend). De productfoto en de 3D-box
    // beschrijven allebei het HELE product (incl. dop), dus fromTop mapt 1-op-1
    // over de volledige box. De eerdere crop-zone-herverdeling was een fudge
    // uit het foute-data-tijdperk en vertekende de positie juist.
    const printTop = box.max.y;
    const printBot = box.min.y;
    const printH = Math.max(0.0001, printTop - printBot);
    const fromTop = Math.max(0, Math.min(1, centroid.yMm / productHeightMm)); // 0=boven, 1=onder

    // Map to 3D space - put marker on front of model (z positive)
    const radius = size.x / 2; // No offset - logo on surface
    // Correctie: centroid 10 mm laten zakken (afstemming met het echte drukvlak)
    const dropUnits = productHeightMm > 0 ? (CENTROID_DROP_MM * size.y) / productHeightMm : 0;
    const height = printTop - fromTop * printH - dropUnits;

    // Position on front of model (angle = 0)
    const x = 0;
    const z = radius;
    const y = height;

    console.log(`Centroid ${centroid.position}: yMm=${centroid.yMm}, productHeight=${productHeightMm}, fromTop=${fromTop.toFixed(3)}, printzone ${printBot.toFixed(3)}..${printTop.toFixed(3)}, 3D y=${y.toFixed(3)}`);

    return new THREE.Vector3(x, y, z);
  };

  // ===== HG WRAP: legt de samengestelde laag-textuur RONDOM het product =====
  // Volgt de ECHTE vorm: per hoogte/hoek wordt het oppervlak geraycast, dus de wrap
  // loopt mee met een taps toelopend of gebogen silhouet (geen rechte cilinder).
  const applyHgWrap = useCallback((dataUrl: string, printWidthMm?: number, printHeightMm?: number, productHeightMm?: number, bottomOffsetMm?: number, heightUrl?: string, reliefMaxMm?: number, view?: { relief?: number; roughness?: number; bump?: number; detailMm?: number }, seamPct?: number) => {
    // Model nog niet geladen? Payload bewaren en na het laden alsnog toepassen.
    if (!sceneRef.current || !modelRef.current) {
      pendingWrapRef.current = dataUrl ? { dataUrl, w: printWidthMm, h: printHeightMm, ph: productHeightMm, bo: bottomOffsetMm, hu: heightUrl, rm: reliefMaxMm, vw: view, sp: seamPct } : null;
      return;
    }

    // Generatie-token: de mesh wordt ASYNC toegevoegd (na texture-load). Zonder token
    // blijven oude wraps in de scene staan bij snelle updates -> je ziet dan nog de
    // vorige lagen op het product.
    const gen = ++hgGenRef.current;

    // Opruimen van oude wraps gebeurt PAS als de nieuwe textuur klaar is (zie onder),
    // anders zie je het product even onbedrukt (zwart) tussen twee updates door.
    const clearStale = () => {
      const stale = sceneRef.current!.children.filter(o => o.name === 'hg_wrap');
      stale.forEach(o => {
        sceneRef.current!.remove(o);
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        const mat = m.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) mat.forEach(x => x.dispose()); else mat?.dispose();
      });
      hgWrapRef.current = null;
    };
    if (!dataUrl) { clearStale(); return; }   // lege wrap: direct schoon product

    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    const prodHmm = productHeightMm || (centroidsData.length > 0 ? centroidsData[0].productHeight : 0);
    const mmPerUnit = prodHmm > 0 ? prodHmm / size.y : 0;
    const hMm = printHeightMm || 0;
    // Bedrukking begint BOVEN de ronde bodem (bottomOffsetMm) en is hMm hoog.
    const bottomMm = bottomOffsetMm || 0;
    const yBot = box.min.y + (mmPerUnit > 0 ? bottomMm / mmPerUnit : 0);
    const yTop = (mmPerUnit > 0 && hMm > 0)
      ? Math.min(box.max.y, yBot + hMm / mmPerUnit)
      : box.max.y;

    // Flessen/mokken zijn rond-symmetrisch: 1 RADIUS-PROFIEL per hoogte is genoeg.
    // (128x97 raycasts blokkeerde de pagina; nu SEG_V x 4 = enkele honderden.)
    // Met reliëf is een veel fijner net nodig: displacement kan alleen bestaande
    // punten verplaatsen. Het RADIUS-PROFIEL blijft wel grof bemeten - dat zijn
    // raycasts en die zijn duur (12.500 stuks liet de pagina eerder vastlopen).
    const HAS_RELIEF = !!heightUrl;
    // Bij reliëf bepaalt de MAASWIJDTE of een stip een vlak plateau wordt of een
    // tentje: displacement verplaatst alleen bestaande punten. Mik op ~0,35 mm per
    // segment, dan liggen er ook over een rasterstip van ~1,4 mm nog een stuk of vier
    // punten en blijft de bovenkant vlak.
    const TARGET_MM = Math.max(0.12, Math.min(1.2, view?.detailMm ?? 0.35));
    const circMm = printWidthMm || 0;
    const bandMm = printHeightMm || 0;
    const SEG_A = HAS_RELIEF
      ? Math.max(384, Math.min(768, Math.round((circMm || 236) / TARGET_MM)))
      : 96;
    const SEG_V = HAS_RELIEF
      ? Math.max(96, Math.min(384, Math.round((bandMm || 116) / TARGET_MM)))
      : Math.max(12, Math.min(48, Math.round((yTop - yBot) / (size.y / 40))));
    const PROF = Math.max(12, Math.min(48, Math.round((yTop - yBot) / (size.y / 40))));
    const ray = new THREE.Raycaster();
    const outR = Math.max(size.x, size.z) * 2;
    const offset = Math.max(size.x, size.z) * 0.006 + (surfaceOffset || 0); // iets ruimer: geen z-fighting
    const fallbackR = Math.max(size.x, size.z) / 2;

    const profile: number[] = [];                        // radius per hoogte-stap
    const probeAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
    for (let iv = 0; iv <= PROF; iv++) {
      const y = yBot + ((yTop - yBot) * iv) / PROF;
      const hitsR: number[] = [];
      for (const a of probeAngles) {
        const from = new THREE.Vector3(center.x + Math.sin(a) * outR, y, center.z + Math.cos(a) * outR);
        const dir = new THREE.Vector3(center.x - from.x, 0, center.z - from.z).normalize();
        ray.set(from, dir);
        const hit = ray.intersectObject(modelRef.current, true)[0];
        if (hit) hitsR.push(Math.hypot(hit.point.x - center.x, hit.point.z - center.z));
      }
      profile.push(hitsR.length ? Math.max(...hitsR) : NaN);
    }
    // Gaten opvullen. LET OP: aan de uiteinden (bodemcurve!) is er geen tweede
    // meetpunt om tussen te interpoleren. Daar de laatste geldige radius licht
    // INKRIMPEN i.p.v. overnemen — anders steekt de wrap onderaan uit.
    for (let i = 0; i < profile.length; i++) {
      if (!isNaN(profile[i])) continue;
      let prev = NaN, next = NaN, prevJ = -1, nextJ = -1;
      for (let j = i - 1; j >= 0; j--) if (!isNaN(profile[j])) { prev = profile[j]; prevJ = j; break; }
      for (let j = i + 1; j < profile.length; j++) if (!isNaN(profile[j])) { next = profile[j]; nextJ = j; break; }
      if (!isNaN(prev) && !isNaN(next)) {
        const t = (i - prevJ) / (nextJ - prevJ);
        profile[i] = prev + (next - prev) * t;      // echt interpoleren
      } else if (!isNaN(prev)) {
        profile[i] = prev * Math.max(0.6, 1 - 0.06 * (i - prevJ)); // taps naar beneden
      } else if (!isNaN(next)) {
        profile[i] = next * Math.max(0.6, 1 - 0.06 * (nextJ - i));
      } else {
        profile[i] = fallbackR;
      }
    }

    const verts: number[] = [];
    const uvs: number[] = [];
    const idx: number[] = [];
    for (let iv = 0; iv <= SEG_V; iv++) {
      const y = yBot + ((yTop - yBot) * iv) / SEG_V;
      // profiel (PROF stappen) uitrekken naar het fijne net (SEG_V stappen)
      const tv = (iv / SEG_V) * PROF;
      const p0 = Math.min(PROF, Math.floor(tv));
      const p1 = Math.min(PROF, p0 + 1);
      const pf = tv - p0;
      const rProf = profile[p0] + (profile[p1] - profile[p0]) * pf;
      for (let ia = 0; ia <= SEG_A; ia++) {
        const a = (ia / SEG_A) * Math.PI * 2;
        const r = rProf + offset;
        verts.push(center.x + Math.sin(a) * r, y, center.z + Math.cos(a) * r);
        uvs.push(ia / SEG_A, iv / SEG_V); // u = rondom, v = onder->boven
      }
    }
    const stride = SEG_A + 1;
    for (let iv = 0; iv < SEG_V; iv++) {
      for (let ia = 0; ia < SEG_A; ia++) {
        const a0 = iv * stride + ia, b0 = a0 + 1, c0 = a0 + stride, d0 = c0 + 1;
        idx.push(a0, c0, b0, b0, c0, d0);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    // Displacement duwt langs de normaal. De winding van deze mesh kan omgekeerd zijn
    // (daarom ook DoubleSide); dan zou het reliëf de fles IN gaan. Steekproef nemen en
    // zo nodig omklappen, zodat de opbouw er altijd uit komt.
    {
      const nA = geo.getAttribute('normal');
      const pA = geo.getAttribute('position');
      const step = Math.max(1, Math.floor(pA.count / 32));
      let inward = 0, tested = 0;
      for (let i = 0; i < pA.count; i += step) {
        const dx = pA.getX(i) - center.x, dz = pA.getZ(i) - center.z;
        if (nA.getX(i) * dx + nA.getZ(i) * dz < 0) inward++;
        tested++;
      }
      if (tested > 0 && inward > tested / 2) {
        for (let i = 0; i < nA.count; i++) nA.setXYZ(i, -nA.getX(i), -nA.getY(i), -nA.getZ(i));
        nA.needsUpdate = true;
        // OOK de winding omdraaien. Alleen de normaal omklappen is niet genoeg: de
        // voorkant van de driehoeken wees dan nog naar binnen, en three.js keert bij
        // DoubleSide de normaal van een achterkant om bij het belichten. Geometrie en
        // belichting stonden dus tegengesteld, waardoor opbouw als een INGEDRUKT
        // reliëf las in plaats van een verhoging.
        const ix = geo.getIndex();
        if (ix) {
          const arr = ix.array as unknown as number[];
          for (let i = 0; i + 2 < arr.length; i += 3) {
            const tmp = arr[i + 1]; arr[i + 1] = arr[i + 2]; arr[i + 2] = tmp;
          }
          ix.needsUpdate = true;
        }
      }
    }

    const loader = new THREE.TextureLoader();
    const loadTex = (url: string) => new Promise<THREE.Texture | null>((res) => {
      loader.load(url, (t) => res(t), undefined, () => res(null));
    });
    Promise.all([loadTex(dataUrl), heightUrl ? loadTex(heightUrl) : Promise.resolve(null)]).then(([tex, hTex]) => {
      if (!tex) return;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      // NAAD-POSITIE: het uitgerolde vlak begint ergens op de omtrek, en precies daar
      // zit de harde rand tussen begin en eind van de druk. Door de u-as te verschuiven
      // draait de hele druk om het product, zodat die naad niet aan de voorkant komt.
      // Alleen de u-as: de hoogte blijft staan waar hij staat.
      const seamU = (((seamPct ?? 0) % 100) + 100) % 100 / 100;
      tex.offset.x = seamU;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
      tex.needsUpdate = true;
      // DoubleSide: de winding van de gegenereerde mesh kan omgekeerd zijn -> anders
      // wordt alles weggecull'd en zie je niets op het product.
      const mat = new THREE.MeshStandardMaterial({
        map: tex, transparent: true, side: THREE.DoubleSide, alphaTest: 0.01,
        // Met echte opbouw hoort de wrap ook diepte te schrijven; anders kijk je
        // dwars door de hogere lagen heen.
        depthWrite: !!hTex,
        metalness: 0.0, roughness: Math.max(0, Math.min(1, view?.roughness ?? 0.82)), polygonOffset: true, polygonOffsetFactor: -6,
      });
      if (hTex) {
        // Hoogtekaart is DATA, geen kleur: NIET naar sRGB omrekenen (standaard is goed).
        hTex.wrapS = THREE.RepeatWrapping;
        hTex.wrapT = THREE.ClampToEdgeWrapping;
        // De hoogtekaart moet exact even ver meedraaien als de kleurenkaart, anders
        // staat het reliëf niet meer op de druk.
        hTex.offset.x = seamU;
        hTex.needsUpdate = true;
        // Weergavefactor voor het reliëf: 1.0 = op ware hoogte. De opbouw is dunner
        // gemaakt in de laag zelf (UNIT_MM in de drawer), niet hier in de weergave,
        // zodat wat je in het menu leest ook is wat je op het product ziet.
        const RELIEF_VIEW_SCALE = Math.max(0.1, Math.min(12, view?.relief ?? 1));
        const maxMm = (reliefMaxMm || 5) * RELIEF_VIEW_SCALE;
        mat.displacementMap = hTex;
        mat.displacementScale = mmPerUnit > 0 ? maxMm / mmPerUnit : 0;
        // Dezelfde kaart als bump, maar HEEL zwak. Een bumpmap schaduwt op de
        // GRADIENT van de hoogtekaart, dus precies op de rand van elke stip. Op 0,25
        // gaf dat een lichte ring rondom elke stip - de "verdikking" die je ziet - en
        // liet hij de stip boller lijken dan hij is. De vorm komt nu van de echte
        // displacement; de bump doet alleen nog een klein beetje randdefinitie.
        // Bumpmap standaard UIT: de vorm komt uit de displacement. Boven 0 legt hij er
        // een tweede, verzonnen rand-schaduw overheen (de ring om elke stip). Instelbaar
        // gemaakt via Instellingen > 3D-weergave.
        const bump = Math.max(0, Math.min(1, view?.bump ?? 0));
        if (bump > 0) { mat.bumpMap = hTex; mat.bumpScale = bump; }
      }
      // Late callback van een verouderde update? Weggooien, niet toevoegen.
      if (gen !== hgGenRef.current) { tex.dispose(); hTex?.dispose(); mat.dispose(); geo.dispose(); return; }
      clearStale();                       // nu pas de vorige wrap weg: geen zwart tussendoor
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = 'hg_wrap';
      mesh.renderOrder = 8;
      sceneRef.current?.add(mesh);
      hgWrapRef.current = mesh;
      // wrap is er: nu pas het product tonen (zie hgWait)
      if (modelRef.current && !modelRef.current.visible) modelRef.current.visible = true;
      setLoading(false);
      console.log(`[HG] wrap om product: ${SEG_A}x${SEG_V} segm, y ${yBot.toFixed(3)}..${yTop.toFixed(3)}, printvlak ${printWidthMm}x${printHeightMm}mm, reliëf ${hTex ? (reliefMaxMm || 5) + 'mm' : 'geen'}`);
      try { window.parent.postMessage({ type: 'hg_wrap_ready' }, '*'); } catch (e) { /* geen parent */ }
    });
  }, [centroidsData, surfaceOffset]);
  applyHgWrapRef.current = applyHgWrap;

  // Remove all centroid markers
  const removeCentroidMarkers = () => {
    if (!sceneRef.current) return;

    centroidMarkersRef.current.forEach(marker => {
      sceneRef.current?.remove(marker);
      if ((marker as THREE.Mesh).geometry) (marker as THREE.Mesh).geometry.dispose();
      if ((marker as THREE.Mesh).material) {
        const mat = (marker as THREE.Mesh).material as THREE.Material;
        mat.dispose();
      }
    });
    centroidMarkersRef.current = [];
  };

  // Generate technique visualization mesh
  const generateTechniqueMesh = useCallback((technique: Technique) => {
    if (!sceneRef.current || !modelRef.current || !meshConfig) return;

    // Remove old mesh
    if (techniqueMeshRef.current) {
      sceneRef.current.remove(techniqueMeshRef.current);
      techniqueMeshRef.current.geometry.dispose();
      (techniqueMeshRef.current.material as THREE.Material).dispose();
    }

    // Get model bounds
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());

    // Calculate mesh dimensions based on config
    const height = size.y * meshConfig.mesh_scale_height;
    const radius = (size.x / 2) * meshConfig.mesh_scale_width;

    // Create cylinder geometry
    const geometry = new THREE.CylinderGeometry(
      radius * meshConfig.mesh_scale_top,
      radius * meshConfig.mesh_scale_bottom,
      height,
      64, 1, true
    );

    // Create semi-transparent material
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      wireframe: false
    });

    const mesh = new THREE.Mesh(geometry, material);

    // Position mesh
    const center = box.getCenter(new THREE.Vector3());
    mesh.position.set(
      center.x + meshConfig.mesh_position_x,
      center.y + meshConfig.mesh_position_y,
      center.z + meshConfig.mesh_position_z
    );

    // Apply rotation
    mesh.rotation.set(
      meshConfig.mesh_rotation_x * Math.PI / 180,
      meshConfig.mesh_rotation_y * Math.PI / 180,
      meshConfig.mesh_rotation_z * Math.PI / 180
    );

    sceneRef.current.add(mesh);
    techniqueMeshRef.current = mesh;

    console.log('Generated technique mesh for:', technique.technique_name);
  }, [meshConfig]);

  // Handle technique selection
  const handleTechniqueSelect = (value: string) => {
    setSelectedTechnique(value);
    if (!isEmbedMode) { try { localStorage.setItem('spranz3d_tech', value); } catch (e) { /* */ } }
    const technique = techniques.find(t => String(t.id) === String(value))
      || techniques.find(t => t.technique_code === value);
    if (technique) {
      generateTechniqueMesh(technique);
    }
  };

  // Handle product selection
  const handleProductSelect = (sku: string) => {
    setSelectedSku(sku);
    setSelectedTechnique('');
    const product = products.find(p => p.variant_sku === sku);
    if (product) {
      loadProduct(product);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;

      // Create texture from uploaded image with high quality settings
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(dataUrl, (texture) => {
        // HIGH QUALITY filtering for sharp textures (same as PHP viewer.js)
        texture.minFilter = THREE.LinearMipmapLinearFilter; // Best quality for zooming
        texture.magFilter = THREE.LinearFilter; // Sharp when viewing close
        texture.generateMipmaps = true; // Enable mipmaps for smooth LOD transitions
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;

        // Enable anisotropic filtering for maximum sharpness at angles
        const maxAnisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
        texture.anisotropy = maxAnisotropy;
        console.log(`Anisotropic filtering: ${maxAnisotropy}x`);

        // Proper alpha handling
        texture.premultiplyAlpha = false;

        // Use correct color space
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;

        setLogoTexture(texture);

        // Apply logo to model
        applyLogoToModel(texture);
        setStatus('Logo toegepast');

        // Set ready flags for screenshot service
        (window as any).__3DReady = true;
        (window as any).__3DLogoApplied = true;
      });
    };
    reader.readAsDataURL(file);
  };

  // Create curved mesh geometry from marker points (same as PHP viewer.js lines 2730-2880)
  // This uses markers to interpolate radius per height, creating a mesh that follows the model's curve
  const createCurvedMeshFromMarkers = (
    markers: MarkerPoint[],
    _config: MeshConfig | null,
    centroidY: number,
    meshHeightMm: number,
    meshWidthMm: number,
    modelHeightMm: number,
    modelSize: THREE.Vector3,
    logoAspectRatio: number = 1.0,
    printAreaWidthMm: number = 0
  ): THREE.BufferGeometry => {
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    // Group markers by height (Y) into rows - same as PHP lines 2736-2749
    const tolerance = 0.001;
    interface MarkerRow {
      height: number;
      markers: MarkerPoint[];
      radius: number;
    }
    const rows: MarkerRow[] = [];

    markers.forEach(marker => {
      let foundRow = rows.find(row => Math.abs(row.height - marker.y) < tolerance);
      if (!foundRow) {
        foundRow = { height: marker.y, markers: [], radius: 0 };
        rows.push(foundRow);
      }
      foundRow.markers.push(marker);
    });

    rows.sort((a, b) => a.height - b.height);

    if (rows.length < 2) {
      console.warn('Not enough marker rows for curved mesh');
      return new THREE.PlaneGeometry(0.1, 0.1);
    }

    // Calculate center point (assume model centered at x=0, z=0)
    const centerX = 0;
    const centerZ = 0;

    // Calculate average radius for each row - same as PHP lines 2752-2761
    rows.forEach(row => {
      let totalRadius = 0;
      row.markers.forEach(marker => {
        const dx = marker.x - centerX;
        const dz = marker.z - centerZ;
        totalRadius += Math.sqrt(dx * dx + dz * dz);
      });
      row.radius = totalRadius / row.markers.length;
    });

    // Calculate scale factor: mm per 3D unit (same as PHP line 2663)
    // This converts between real-world mm and 3D model units
    const scaleFactorMM = modelHeightMm / modelSize.y;

    // Calculate mesh height in 3D units (same as PHP line 2667)
    const meshHeight3D = meshHeightMm / scaleFactorMM;
    const meshMinY = centroidY - meshHeight3D / 2;
    const meshMaxY = centroidY + meshHeight3D / 2;

    const segmentsH = 64;
    const segmentsV = Math.max(8, Math.ceil(meshHeightMm / 5));
    const techniqueOffset = surfaceOffset; // Controlled by slider

    // Calculate average radius for arc angle calculation
    let avgRadius = 0;
    rows.forEach(row => avgRadius += row.radius);
    avgRadius = avgRadius / rows.length + techniqueOffset;

    // Calculate mesh width in 3D units
    const meshWidth3D = meshWidthMm / scaleFactorMM;

    // Calculate arc angle based on actual mesh width (arc length = width in mm converted to 3D)
    // arcAngle = arcLength / radius
    const arcAngle = meshWidth3D / avgRadius;

    // Verify dimensions
    const arcLength3D = avgRadius * arcAngle;
    console.log(`MESH CALC: modelHeightMm=${modelHeightMm}mm, modelSize.y=${modelSize.y.toFixed(4)} 3D units`);
    console.log(`MESH CALC: meshWidthMm=${meshWidthMm.toFixed(1)}mm, meshHeightMm=${meshHeightMm.toFixed(1)}mm`);
    console.log(`MESH CALC: avgRadius=${avgRadius.toFixed(4)}, arcAngle=${(arcAngle * 180 / Math.PI).toFixed(1)}deg (max 360°)`);
    console.log(`MESH CALC: arcLength3D=${arcLength3D.toFixed(4)}, circumference=${(2 * Math.PI * avgRadius).toFixed(4)} 3D units`);

    // x-offset exact zoals de 2D: mm binnen het drukvlak via de werkelijke
    // straal (zelfde fix als de cylinder-tak; fractie-van-de-omtrek vergrootte
    // kleine 2D-verschuivingen enorm uit).
    const mmPerUnitMk = modelHeightMm > 0 ? modelHeightMm / modelSize.y : 0;
    const radiusMmMk = mmPerUnitMk > 0 ? avgRadius * mmPerUnitMk : 0;
    const xOffsetMmMk = (logoOffset.x - 0.5) * (printAreaWidthMm || 0);
    const centroidAngle = radiusMmMk > 0 ? xOffsetMmMk / radiusMmMk : 0;
    const angleStart = centroidAngle - arcAngle / 2;
    const angleEnd = centroidAngle + arcAngle / 2;

    console.log(`MARKER: logoOffset.x=${logoOffset.x.toFixed(2)} → centroidAngle=${(centroidAngle * 180 / Math.PI).toFixed(1)}deg`);

    // Helper function to get radius at any Y by interpolating marker rows - same as PHP lines 2815-2841
    const getRadiusAtY = (y: number): number => {
      let below: MarkerRow | null = null;
      let above: MarkerRow | null = null;

      for (const row of rows) {
        if (row.height <= y) {
          if (!below || row.height > below.height) below = row;
        }
        if (row.height >= y) {
          if (!above || row.height < above.height) above = row;
        }
      }

      if (below && above && below.height !== above.height) {
        const t = (y - below.height) / (above.height - below.height);
        return (below.radius + (above.radius - below.radius) * t) + techniqueOffset;
      } else if (below) {
        return below.radius + techniqueOffset;
      } else if (above) {
        return above.radius + techniqueOffset;
      }
      return (modelSize.x / 2) + techniqueOffset; // Fallback
    };

    // Generate vertices - same as PHP lines 2843-2860
    // Mesh is now correctly sized to match logo aspect ratio - simple 0-1 UV mapping
    for (let i = 0; i <= segmentsV; i++) {
      const t = i / segmentsV;
      const y = meshMinY + (meshMaxY - meshMinY) * t;
      const radius = getRadiusAtY(y);

      for (let j = 0; j <= segmentsH; j++) {
        const angle = angleStart + (angleEnd - angleStart) * (j / segmentsH);
        const x = centerX + Math.cos(angle) * radius;
        const z = centerZ + Math.sin(angle) * radius;

        vertices.push(x, y, z);
        // Simple 0-1 UV mapping - mesh aspect ratio matches logo
        // Flip U coordinate to show logo on outside
        uvs.push(1 - (j / segmentsH), t);
      }
    }

    // Create indices - same as PHP lines 2863-2873
    for (let i = 0; i < segmentsV; i++) {
      for (let j = 0; j < segmentsH; j++) {
        const a = i * (segmentsH + 1) + j;
        const b = a + segmentsH + 1;
        const c = a + 1;
        const d = b + 1;
        indices.push(a, b, d);
        indices.push(a, d, c);
      }
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    console.log(`Curved mesh from markers: ${rows.length} marker rows, Y range ${meshMinY.toFixed(3)} to ${meshMaxY.toFixed(3)}, arc=${(arcAngle * 180 / Math.PI).toFixed(1)}deg`);

    return geometry;
  };

  // Apply logo texture to model using print area constraints
  const applyLogoToModel = useCallback((texture: THREE.Texture, placement?: { technique?: any; scale?: { scaleX: number; scaleY: number }; offset?: { x: number; y: number }; append?: boolean }) => {
    if (!sceneRef.current || !modelRef.current) return;

    // Identieke rebuild overslaan: decals zijn losse scene-objecten en blijven
    // geldig, ook na een kleurwissel van hetzelfde artikel. Drie identieke
    // builds achter elkaar kostten seconden zonder zichtbaar verschil.
    if (!placement?.append) {
      const img = (texture as any).image;
      const src = img && img.src ? String(img.src) : '';
      const buildSig = [String(selectedTechnique), src.length, src.slice(0, 24), logoScale.scaleX, logoScale.scaleY, logoOffset.x, logoOffset.y, surfaceOffset].join('|');
      if (buildSig === lastBuildSigRef.current && logoMeshRef.current) {
        try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
        return;
      }
      lastBuildSigRef.current = buildSig;
    }

    // Remove old logo mesh (niet bij append: extra decal komt ERBIJ)
    // removeFromParent: een dop-mesh hangt aan de dop-node, niet aan de scene.
    if (!placement?.append) {
      if (logoMeshRef.current) {
        logoMeshRef.current.removeFromParent();
        logoMeshRef.current.geometry.dispose();
        (logoMeshRef.current.material as THREE.Material).dispose();
        logoMeshRef.current = null;
      }
      for (const m of extraLogoMeshesRef.current) {
        m.removeFromParent();
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      extraLogoMeshesRef.current = [];
    }

    // Get model bounds
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Get the selected technique's print area dimensions.
    // Textiel heeft DEZELFDE code op meerdere posities (DTF voor/borst/...) —
    // selectedTechnique kan daarom ook een rij-ID zijn; match eerst op id.
    const technique = placement?.technique
      || techniques.find(t => String(t.id) === String(selectedTechnique))
      || techniques.find(t => t.technique_code === selectedTechnique);
    const effOffset = placement?.offset ?? logoOffset;

    // Calculate logo dimensions based on technique print area or defaults
    let printAreaWidth = 50; // Default in mm
    let printAreaHeight = 30; // Default in mm

    if (technique) {
      // Parse print area string like "223x50" or use position_1 dimensions
      if (technique.print_area) {
        // '223x50' (Spranz) én '270 x 300 mm' (XD/PF): gewoon split('x') gaf NaN -> standaard 50x30 mm
        const paM = String(technique.print_area).match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
        const [w, h] = paM ? [parseFloat(paM[1].replace(',', '.')), parseFloat(paM[2].replace(',', '.'))] : [NaN, NaN];
        if (w && h) {
          printAreaWidth = w;
          printAreaHeight = h;
        }
      } else if (technique.position_1_width && technique.position_1_height) {
        printAreaWidth = technique.position_1_width;
        printAreaHeight = technique.position_1_height;
      }
    }

    // Calculate the aspect ratio of the uploaded logo
    const logoAspect = texture.image.width / texture.image.height;
    const printAreaAspect = printAreaWidth / printAreaHeight;

    // Fit logo within print area, maintaining logo aspect ratio
    let logoWidthMm: number;
    let logoHeightMm: number;

    if (logoAspect > printAreaAspect) {
      // Logo is wider than print area - fit by width
      logoWidthMm = printAreaWidth;
      logoHeightMm = printAreaWidth / logoAspect;
    } else {
      // Logo is taller than print area - fit by height
      logoHeightMm = printAreaHeight;
      logoWidthMm = printAreaHeight * logoAspect;
    }

    // Convert mm to 3D units (assuming model is normalized to ~1 unit)
    // Use mesh_crop values to determine available print zone
    const cropTop = meshConfig ? parseFloat(String(meshConfig.mesh_crop_top)) || 0 : 0;
    const cropBottom = meshConfig ? parseFloat(String(meshConfig.mesh_crop_bottom)) || 0 : 0;

    const printableHeight = size.y * (1 - cropTop - cropBottom);
    const printableYStart = box.min.y + (size.y * cropBottom);
    const printableYCenter = printableYStart + (printableHeight / 2);

    // Scale logo to fit in printable area
    // Use logoScale from modal to match 2D canvas scaling (scaleX/scaleY are fractions of print area)
    const maxLogoHeight = printableHeight * 0.8; // Use 80% of printable area max
    const scaleFactor = Math.min(maxLogoHeight / (logoHeightMm / 1000), size.x * 0.8 / (logoWidthMm / 1000));

    // Apply a reasonable scale based on model size, then apply user's scale from 2D canvas
    const userScaleX = placement?.scale?.scaleX ?? logoScale.scaleX;
    const userScaleY = placement?.scale?.scaleY ?? logoScale.scaleY;
    console.log(`Logo scale from modal: scaleX=${userScaleX.toFixed(2)}, scaleY=${userScaleY.toFixed(2)}`);

    const logoHeight3D = (logoHeightMm / 1000) * scaleFactor * 10 * userScaleY; // Apply user's Y scale
    const logoWidth3D = (logoWidthMm / 1000) * scaleFactor * 10 * userScaleX; // Apply user's X scale

    // Create logo mesh based on mesh config type
    // ── PARAPLU: logo als decal op de koepel (grootste doek-mesh), plat op de
    //    gebogen stof via raycast + oppervlakte-normaal (zoals het shirt). ──
    if (meshConfig?.product_type === 'paraplu') {
      // Grootste horizontale mesh = de koepel (doek); by-name kan de dunne bies pakken.
      let domeMesh: any = null; let maxV = 0;
      modelRef.current.traverse((o: any) => {
        if (o.isMesh) {
          const v = o.geometry?.attributes?.position?.count || 0;
          if (v > maxV) { maxV = v; domeMesh = o; } // koepel-doek = meeste vertices
        }
      });
      if (domeMesh) {
        const cbox = new THREE.Box3().setFromObject(domeMesh);
        const csize = cbox.getSize(new THREE.Vector3());
        const ccenter = cbox.getCenter(new THREE.Vector3());
        // FULL SIZE (digitale sublimatiedruk): artwork over de HELE canopy —
        // top-down projectie die de koepel volledig bedekt.
        const isFullCanopy = technique && String(technique.technique_code) === 'FULLSIZE';
        if (isFullCanopy) {
          // Projectie-box gecentreerd op het koepel-MIDDEN met ruime diepte,
          // anders valt de onderste helft van de koepel buiten de box.
          const projPos = new THREE.Vector3(ccenter.x, ccenter.y, ccenter.z);
          const projRot = new THREE.Euler(-Math.PI / 2, 0, 0);
          const fSize = new THREE.Vector3(csize.x, csize.z, csize.y * 4 + 0.5);
          const fgeo = new DecalGeometry(domeMesh as THREE.Mesh, projPos, projRot, fSize);
          // Doek-normalen wijzen omlaag: zonder flip leest de BOVENKANT gespiegeld.
          // U-as flippen -> bovenkant correct, binnenkant spiegelbeeld (zoals echte sublimatie).
          const fuv = fgeo.getAttribute('uv');
          if (fuv) { for (let fi = 0; fi < fuv.count; fi++) fuv.setX(fi, 1 - fuv.getX(fi)); fuv.needsUpdate = true; }
          const fmat = new THREE.MeshStandardMaterial({
            map: texture, transparent: true, roughness: 0.9,
            polygonOffset: true, polygonOffsetFactor: -4, depthWrite: false,
            side: THREE.DoubleSide,
          });
          const fmesh = new THREE.Mesh(fgeo, fmat);
          fmesh.renderOrder = 5;
          if (placement?.append) { extraLogoMeshesRef.current.push(fmesh); } else { logoMeshRef.current = fmesh; }
          sceneRef.current.add(fmesh);
          try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
          console.log('[3D] FULL-CANOPY sublimatie over koepel', { w: +csize.x.toFixed(2), d2: +csize.z.toFixed(2) });
          return;
        }
        const uScaleX = placement?.scale?.scaleX ?? logoScale.scaleX;
        const decalW = csize.x * 0.28 * (uScaleX || 1);
        const decalH = decalW / (logoAspect || 1);
        const targetY = cbox.min.y + csize.y * 0.45;
        // Paneel-midden kiezen via de RIB-meshes van het model (Rib0..Rib7):
        // ribben = naden; het logo hoort MIDDEN OP een paneel, op het paneel
        // dat naar de init-camera wijst (45 graden in het xz-vlak).
        const ribAngles = [];
        modelRef.current.traverse((o) => {
          const oa = o;
          if (oa.isMesh && /^rib/i.test(oa.name || '')) {
            const rb = new THREE.Box3().setFromObject(oa);
            const rcn = rb.getCenter(new THREE.Vector3());
            ribAngles.push(Math.atan2(rcn.z - ccenter.z, rcn.x - ccenter.x));
          }
        });
        // Richt op de ECHTE camera-hoek (niet een vaste 45): het logo hoort op
        // het paneel dat de kijker op dat moment ziet.
        const camPos = cameraRef.current ? cameraRef.current.position : new THREE.Vector3(1.75, 1, 1.75);
        const camA = Math.atan2(camPos.z - ccenter.z, camPos.x - ccenter.x);
        let panelAngle = camA;
        if (ribAngles.length >= 2) {
          ribAngles.sort((a, b) => a - b);
          let bestD = Infinity;
          for (let ri = 0; ri < ribAngles.length; ri++) {
            const a1 = ribAngles[ri];
            const a2 = ri + 1 < ribAngles.length ? ribAngles[ri + 1] : ribAngles[0] + Math.PI * 2;
            const mid = (a1 + a2) / 2;
            const dd = Math.abs(Math.atan2(Math.sin(mid - camA), Math.cos(mid - camA)));
            if (dd < bestD) { bestD = dd; panelAngle = mid; }
          }
        }
        const rr = Math.max(csize.x, csize.z);
        const rayOrigin = new THREE.Vector3(
          ccenter.x + Math.cos(panelAngle) * rr,
          targetY,
          ccenter.z + Math.sin(panelAngle) * rr
        );
        const rayDir = new THREE.Vector3(ccenter.x - rayOrigin.x, 0, ccenter.z - rayOrigin.z).normalize();
        const rc = new THREE.Raycaster(rayOrigin, rayDir);
        const hits = rc.intersectObject(domeMesh as THREE.Mesh, true);
        if (hits.length > 0) {
          const hit = hits[0];
          const hitMesh = hit.object as THREE.Mesh;
          let n = hit.face
            ? hit.face.normal.clone().transformDirection(hitMesh.matrixWorld)
            : new THREE.Vector3(0, 0, 1);
          if (n.dot(hit.point.clone().sub(ccenter)) < 0) n = n.negate();
          const decalPoint = hit.point.clone().add(n.clone().multiplyScalar(0.006));
          const helper = new THREE.Object3D();
          helper.position.copy(decalPoint);
          helper.lookAt(decalPoint.clone().add(n));
          const decalSize = new THREE.Vector3(decalW, decalH, Math.max(decalW, decalH) * 1.4);
          const dgeo = new DecalGeometry(hitMesh, decalPoint, helper.rotation, decalSize);
          const dmat = new THREE.MeshStandardMaterial({
            map: texture, transparent: true, alphaTest: 0.02, roughness: 0.9,
            side: THREE.DoubleSide, depthWrite: false, depthTest: false,
            polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
          });
          const dmesh = new THREE.Mesh(dgeo, dmat);
          dmesh.renderOrder = 10;
          if (placement?.append) { extraLogoMeshesRef.current.push(dmesh); } else { logoMeshRef.current = dmesh; }
          sceneRef.current.add(dmesh);
          try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
          console.log('[3D] PARAPLU decal ' + JSON.stringify({ mesh: String(domeMesh.name), hit: hit.point.toArray().map((v:number)=>+v.toFixed(3)), verts: (dgeo.getAttribute('position') as any)?.count || 0 }));
          return;
        }
      }
    }

    // ── PF CONCEPT (mesh_type 'imprint'): het GLB heeft per printvlak een eigen "imprint-<areaId>"-mesh
    //    waarvan de UV's precies het vlak in de textuur beslaan. Het logo wordt in dat UV-vlak op een
    //    canvas getekend en als textuur op het imprint-materiaal gezet; positie/maat zijn fracties van
    //    het vlak, net als in de 2D-canvas. Geen centroids/mm-projectie nodig. ──
    if (meshConfig && String((meshConfig as any).mesh_type) === 'imprint') {
      const areaKey = String((technique as any)?.side || '');
      const imp = pfImprintsRef.current.get(areaKey)
        || (pfImprintsRef.current.size === 1 ? Array.from(pfImprintsRef.current.values())[0] : undefined);
      if (!imp) {
        console.warn('[3D] PF: geen imprint-mesh voor vlak "' + areaKey + '" (beschikbaar: ' + Array.from(pfImprintsRef.current.keys()).join(', ') + ')');
        try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
        return;
      }
      if (!placement?.append) {
        // nieuwe hoofdplaatsing: andere vlakken leegmaken (extra's komen via append terug)
        pfImprintsRef.current.forEach((v, k) => {
          if (k !== areaKey && v.material.map !== v.blank) { v.material.map = v.blank; v.material.needsUpdate = true; }
        });
        pfExtraAreasRef.current.clear();
      } else {
        pfExtraAreasRef.current.add(areaKey);
      }
      // UV-omvang van de imprint-mesh = het printvlak in textuurruimte (glTF: v=0 is de bovenkant)
      const uvAttr = imp.mesh.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
      let u0 = 0, v0 = 0, u1 = 1, v1 = 1;
      if (uvAttr && uvAttr.count > 0) {
        u0 = Infinity; v0 = Infinity; u1 = -Infinity; v1 = -Infinity;
        for (let i = 0; i < uvAttr.count; i++) {
          const uu = uvAttr.getX(i), vv = uvAttr.getY(i);
          if (uu < u0) u0 = uu; if (uu > u1) u1 = uu; if (vv < v0) v0 = vv; if (vv > v1) v1 = vv;
        }
      }
      const TEX = 2048;
      const rx = u0 * TEX, ry = v0 * TEX, rw = Math.max(1, (u1 - u0) * TEX), rh = Math.max(1, (v1 - v0) * TEX);
      // logo-maat eerst in mm (zelfde regels als de andere takken), dan naar het UV-vlak schalen
      // fysieke maat van het vlak in mm uit print_area ("30 x 70 mm"); anders de UV-verhouding.
      // px/mm kan per as verschillen (UV-rect is niet per se op schaal) -> apart schalen, zodat
      // het logo zijn eigen verhouding houdt (geen uitrekking).
      const paM = String((technique as any)?.print_area || '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
      const areaWmm = paM ? parseFloat(paM[1].replace(',', '.')) : (printAreaWidth || 50);
      const areaHmm = paM ? parseFloat(paM[2].replace(',', '.')) : areaWmm * (rh / rw);
      const pxPerMmX = rw / Math.max(areaWmm, 0.1), pxPerMmY = rh / Math.max(areaHmm, 0.1);
      const la = logoAspect || 1;
      let wMm: number;
      if (hasModalScaleRef.current || placement?.scale) {
        wMm = areaWmm * userScaleX;                       // fractie van de vlakbreedte, zoals de 2D-canvas
      } else {
        wMm = (la > areaWmm / areaHmm) ? areaWmm : areaHmm * la;   // passend in het vlak
        wMm *= userScaleX;
      }
      const hMm = wMm / la;
      const lw = wMm * pxPerMmX;
      const lh = hMm * pxPerMmY;
      const ox = (effOffset && typeof effOffset.x === 'number') ? effOffset.x : 0.5;
      const oy = (effOffset && typeof effOffset.y === 'number') ? effOffset.y : 0.5;
      const cx = rx + rw * ox, cy = ry + rh * oy;
      const canvas = document.createElement('canvas'); canvas.width = TEX; canvas.height = TEX;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, TEX, TEX);
        ctx.save(); ctx.beginPath(); ctx.rect(rx, ry, rw, rh); ctx.clip();
        try { ctx.drawImage(texture.image as CanvasImageSource, cx - lw / 2, cy - lh / 2, lw, lh); }
        catch (e) { console.warn('[3D] PF drawImage mislukt', e); }
        ctx.restore();
      }
      const ctex = new THREE.CanvasTexture(canvas);
      ctex.flipY = false;
      ctex.colorSpace = THREE.SRGBColorSpace;
      ctex.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
      ctex.minFilter = THREE.LinearMipmapLinearFilter; ctex.magFilter = THREE.LinearFilter;
      ctex.needsUpdate = true;
      if (imp.material.map && imp.material.map !== imp.blank) { try { imp.material.map.dispose(); } catch (e) { /* al weg */ } }
      imp.material.map = ctex; imp.material.transparent = true; imp.material.needsUpdate = true;
      imp.mesh.visible = true;
      // camera recht voor het vlak zetten (hoogte en afstand behouden) — alleen bij een ander vlak
      if (!placement?.append && pfCamAreaRef.current !== areaKey && cameraRef.current && controlsRef.current) {
        pfCamAreaRef.current = areaKey;
        try {
          const wc = new THREE.Box3().setFromObject(imp.mesh).getCenter(new THREE.Vector3());
          const mc = new THREE.Box3().setFromObject(modelRef.current).getCenter(new THREE.Vector3());
          const dir = new THREE.Vector3(wc.x - mc.x, 0, wc.z - mc.z);
          if (dir.lengthSq() < 1e-10) dir.set(0, 0, 1);
          dir.normalize();
          const cam = cameraRef.current, ctr = controlsRef.current as any;
          const tgt: THREE.Vector3 = ctr.target ? ctr.target.clone() : mc.clone();
          const dist = cam.position.distanceTo(tgt);
          const y = cam.position.y;
          cam.position.copy(tgt.clone().add(dir.multiplyScalar(dist)));
          cam.position.y = y;
          cam.lookAt(tgt);
          if (ctr.update) ctr.update();
        } catch (e) { /* camera laten staan */ }
      }
      console.log('[3D] PF imprint "' + areaKey + '" uv=' + [u0, v0, u1, v1].map(n => n.toFixed(3)).join(',') + ' logo=' + lw.toFixed(0) + 'x' + lh.toFixed(0) + 'px');
      try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
      return;
    }

    let geometry: THREE.BufferGeometry;
    let isDecal = false;
    const meshType = meshConfig?.mesh_type || 'flat';
    const markerPoints = meshConfig?.marker_points;

    console.log(`Logo mesh: meshType=${meshType}, markerPoints=${markerPoints?.length || 0}, meshConfig exists=${!!meshConfig}`);

    if ((meshType === 'curved') && markerPoints && markerPoints.length >= 10) {
      // Find the centroid marker for this technique's position to get correct Y
      const techniquePosition = technique?.position?.toLowerCase() || 'midden';
      // 29-07: de "DOC58"-override (eigen 595/210 + 80%-fill-normalisatie op de
      // ruwe y_position_1) is verwijderd. Die compenseerde het oude, foute
      // centroids-endpoint; sinds de exacte mm/px-terugrekening in de backend
      // is de centroid-marker (crop-aware, zelfde bron als cylinder-pad) de
      // waarheid. Op 376-00 zette DOC58 het logo precies ~44mm te laag.
      const centroidMarker = centroidMarkersRef.current.find(m => m.name === `centroid_${techniquePosition}`);
      const baseCentroidY: number = centroidMarker ? centroidMarker.position.y : printableYCenter;
      // Get product dimensions from centroid data (set when loading centroids from API)
      // Try exact match first, then first centroid (all have same product dimensions)
      // LET OP: dit blok staat bewust VÓÓR de sfmmY-berekening — modelHeightMm werd
      // eerder ná gebruik gedeclareerd (TDZ ReferenceError: curved mesh crashte).
      let matchingCentroid = centroidsData.find(c => c.position.toLowerCase() === techniquePosition);
      if (!matchingCentroid && centroidsData.length > 0) {
        matchingCentroid = centroidsData[0];
      }

      if (!matchingCentroid) {
        console.error('No centroid data available - cannot calculate mesh size');
        return;
      }

      // Get product dimensions from centroid data
      const modelHeightMm = matchingCentroid.productHeight;
      const _productDiameterMm = matchingCentroid.productDiameter;

      // De 2D geeft relY door als fractie van het PRINTVLAK, niet van het hele
      // model. `size.y * 0.3` was een gok waardoor 2D en 3D verticaal uit elkaar
      // liepen; met de mm-schaal komt de verschuiving exact overeen met de 2D
      // (dezelfde aanpak die de flat-mesh hieronder al gebruikt).
      const sfmmY = modelHeightMm > 0 ? modelHeightMm / size.y : 0;
      const yOffsetAmount = sfmmY > 0
        ? (effOffset.y - 0.5) * (printAreaHeight / sfmmY)
        : (effOffset.y - 0.5) * size.y * 0.3;
      const centroidY = baseCentroidY - yOffsetAmount; // Subtract because 3D Y is up, 2D Y is down
      console.log(`Markers: Y offset=${yOffsetAmount.toFixed(4)}, baseCentroidY=${baseCentroidY.toFixed(4)}, final centroidY=${centroidY.toFixed(4)}`);

      console.log(`CENTROID: position="${techniquePosition}", productHeight=${modelHeightMm}mm, productDiameter=${_productDiameterMm}mm`);
      console.log(`MESH: printArea=${printAreaWidth}x${printAreaHeight}mm, modelSize.y=${size.y.toFixed(4)}`);

      // Use LOGO dimensions scaled to fit within print area while maintaining aspect ratio
      const isPortraitPrintArea = printAreaHeight > printAreaWidth;
      const maxWidthMm = isPortraitPrintArea ? printAreaHeight : printAreaWidth;
      const maxHeightMm = isPortraitPrintArea ? printAreaWidth : printAreaHeight;

      // Scale logo to fit within print area bounds while maintaining aspect ratio
      const printAreaAspect = maxWidthMm / maxHeightMm;
      let meshWidthMm: number;
      let meshHeightMm: number;

      if (logoAspect > printAreaAspect) {
        // Logo is wider - fit by width
        meshWidthMm = maxWidthMm;
        meshHeightMm = maxWidthMm / logoAspect;
      } else {
        // Logo is taller - fit by height
        meshHeightMm = maxHeightMm;
        meshWidthMm = maxHeightMm * logoAspect;
      }

      // Apply user scale from 2D canvas (scaleX/scaleY are fractions of print area)
      // Het 2D-canvas heeft de verhouding AL bepaald: scaleX/scaleY zijn de breedte en
      // de hoogte van het logo als fractie van het printvlak. De aspect-fit hierboven
      // daar nog eens mee vermenigvuldigen krimpt de hoogte een tweede keer, waardoor
      // het logo plat op het product komt. Geeft de modal een schaal door, dan is die
      // dus leidend en gebruiken we het printvlak rechtstreeks.
      if (hasModalScaleRef.current || placement?.scale) {
        // De breedte volgt de schaal uit het 2D-canvas; de HOOGTE volgt uit de
        // verhouding van het beeld zelf. printAreaHeight wordt hier bewust niet
        // gebruikt: zolang de techniekenlijst nog niet geladen is valt die terug
        // op de standaard 50x30 mm, en dan komt het logo vervormd op het product.
        // Zo kan de verhouding per definitie niet meer afwijken van de 2D.
        meshWidthMm = printAreaWidth * userScaleX;
        meshHeightMm = meshWidthMm / (logoAspect || 1);
      } else {
        meshWidthMm = meshWidthMm * userScaleX;
        meshHeightMm = meshHeightMm * userScaleY;
      }

      // CRITICAL: Limit mesh width to print area width - logo cannot be wider than print area!
      if (meshWidthMm > maxWidthMm) {
        console.warn(`Marker mesh width ${meshWidthMm.toFixed(1)}mm exceeds print area ${maxWidthMm}mm - clamping!`);
        meshWidthMm = maxWidthMm;
      }

      console.log(`MARKERS LOGO MESH: logo aspect=${logoAspect.toFixed(2)}, print area=${maxWidthMm}x${maxHeightMm}mm, mesh=${meshWidthMm.toFixed(1)}x${meshHeightMm.toFixed(1)}mm (userScale: ${userScaleX.toFixed(2)}x${userScaleY.toFixed(2)})`);

      // The mesh is sized to match logo aspect ratio - no UV scaling needed
      geometry = createCurvedMeshFromMarkers(
        markerPoints,
        meshConfig,
        centroidY,
        meshHeightMm,
        meshWidthMm,
        modelHeightMm,
        size,
        logoAspect,
        printAreaWidth
      );
      console.log(`Created curved mesh from ${markerPoints.length} marker points at Y=${centroidY.toFixed(4)}, mesh: ${meshWidthMm.toFixed(1)}x${meshHeightMm.toFixed(1)}mm`);
    } else if (meshType === 'cylinder' || meshType === 'curved') {
      // Create a curved rectangular mesh that wraps around the front of the cylinder
      // Same approach as PHP viewer.js generateTechniqueMesh (lines 2680-2728)

      // Get product dimensions from centroidsData
      const fallbackCentroid = centroidsData.length > 0 ? centroidsData[0] : null;
      if (!fallbackCentroid) {
        console.error('No centroid data available - cannot calculate mesh size');
        return;
      }

      const diameterMm = fallbackCentroid.productDiameter;
      const circumferenceMm = Math.PI * diameterMm;
      const modelHeightMm = fallbackCentroid.productHeight;

      // Use LOGO dimensions scaled to fit within print area while maintaining aspect ratio
      // This prevents stretching/distortion of the logo
      const isPortraitPrintArea = printAreaHeight > printAreaWidth;
      const maxWidthMm = isPortraitPrintArea ? printAreaHeight : printAreaWidth;
      const maxHeightMm = isPortraitPrintArea ? printAreaWidth : printAreaHeight;

      // Scale logo to fit within print area bounds while maintaining aspect ratio
      const printAreaAspect = maxWidthMm / maxHeightMm;
      let meshWidthMm: number;
      let meshHeightMm: number;

      if (logoAspect > printAreaAspect) {
        // Logo is wider - fit by width, scale height proportionally
        meshWidthMm = maxWidthMm;
        meshHeightMm = maxWidthMm / logoAspect;
      } else {
        // Logo is taller - fit by height, scale width proportionally
        meshHeightMm = maxHeightMm;
        meshWidthMm = maxHeightMm * logoAspect;
      }

      // Apply user scale from 2D canvas (scaleX/scaleY are fractions of print area)
      // Het 2D-canvas heeft de verhouding AL bepaald: scaleX/scaleY zijn de breedte en
      // de hoogte van het logo als fractie van het printvlak. De aspect-fit hierboven
      // daar nog eens mee vermenigvuldigen krimpt de hoogte een tweede keer, waardoor
      // het logo plat op het product komt. Geeft de modal een schaal door, dan is die
      // dus leidend en gebruiken we het printvlak rechtstreeks.
      if (hasModalScaleRef.current || placement?.scale) {
        // De breedte volgt de schaal uit het 2D-canvas; de HOOGTE volgt uit de
        // verhouding van het beeld zelf. printAreaHeight wordt hier bewust niet
        // gebruikt: zolang de techniekenlijst nog niet geladen is valt die terug
        // op de standaard 50x30 mm, en dan komt het logo vervormd op het product.
        // Zo kan de verhouding per definitie niet meer afwijken van de 2D.
        meshWidthMm = printAreaWidth * userScaleX;
        meshHeightMm = meshWidthMm / (logoAspect || 1);
      } else {
        meshWidthMm = meshWidthMm * userScaleX;
        meshHeightMm = meshHeightMm * userScaleY;
      }

      // CRITICAL: Limit mesh width to print area width - logo cannot be wider than print area!
      // Print area width (232mm for DP-360-XL) is the maximum arc length allowed
      if (meshWidthMm > maxWidthMm) {
        console.warn(`Mesh width ${meshWidthMm.toFixed(1)}mm exceeds print area ${maxWidthMm}mm - clamping!`);
        meshWidthMm = maxWidthMm;
      }

      console.log(`LOGO MESH: logo aspect=${logoAspect.toFixed(2)}, print area=${maxWidthMm}x${maxHeightMm}mm, mesh=${meshWidthMm.toFixed(1)}x${meshHeightMm.toFixed(1)}mm (userScale: ${userScaleX.toFixed(2)}x${userScaleY.toFixed(2)})`);

      // Calculate radius in 3D units (model radius + surface offset from slider)
      // Straal: uit de ECHTE productdiameter (product_size), niet uit de
      // bounding box — die telt uitsteeksels als een draagband (332-00) of
      // oor mee, waardoor het logo op de band landde i.p.v. op de fles.
      // Bij "auf Deckel" geldt de straal van de dop-node (karaf 133-00).
      let radiusBasis = size.x / 2;
      const mmPerUnitR = modelHeightMm > 0 ? modelHeightMm / size.y : 0;
      if (mmPerUnitR > 0 && diameterMm > 0) {
        const dataRadius = (diameterMm / 2) / mmPerUnitR;
        if (dataRadius > 0.001 && dataRadius < radiusBasis) {
          // Ligt de box maar iets (< 8%) buiten de productdiameter, dan is dat
          // geen draagband of oor maar een gebogen romp (376-00 bolt in het
          // midden uit): op de diameter-straal zou het logo dan IN de romp
          // verdwijnen. Alleen bij een duidelijk uitsteeksel de datastraal nemen.
          if (radiusBasis / dataRadius < 1.08) {
            console.log(`[3D] Box-straal aangehouden: ${radiusBasis.toFixed(4)} (productdiameter ${diameterMm}mm geeft ${dataRadius.toFixed(4)}, verschil < 8%)`);
          } else {
            console.log(`[3D] Straal uit productdiameter: ${dataRadius.toFixed(4)} (box was ${radiusBasis.toFixed(4)}, diameter ${diameterMm}mm)`);
            radiusBasis = dataRadius;
          }
        }
      }
      const posVoorRadius = String(technique?.position || '').toLowerCase();
      if (posVoorRadius.includes('deckel') && capNodeRef.current) {
        const capBox = new THREE.Box3().setFromObject(capNodeRef.current);
        const capSize = capBox.getSize(new THREE.Vector3());
        if (capSize.x > 0.001) {
          radiusBasis = capSize.x / 2;
          console.log(`[3D] Deckel-straal uit dop-node: ${radiusBasis.toFixed(4)} (romp: ${(size.x / 2).toFixed(4)})`);
        }
      }
      const radius = radiusBasis + surfaceOffset;

      // Calculate mesh dimensions in 3D units using scale factor
      const scaleFactorMM = modelHeightMm / size.y;
      const meshHeight3D = meshHeightMm / scaleFactorMM;
      const meshWidth3D = meshWidthMm / scaleFactorMM;

      // Calculate arc angle based on actual mesh width (arc length = width in mm)
      // arcAngle = arcLength / radius
      const arcAngle = meshWidth3D / radius;

      // x-offset EXACT zoals de 2D: millimeters binnen het drukvlak, omgerekend
      // naar boog via de werkelijke straal — niet als fractie van de hele
      // omtrek (dat vergrootte een kleine 2D-verschuiving tot tientallen
      // graden, zichtbaar op de 133-dop).
      const mmPerUnitX = modelHeightMm > 0 ? modelHeightMm / size.y : 0;
      const radiusMmX = mmPerUnitX > 0 ? radius * mmPerUnitX : 0;
      const xOffsetMm = (effOffset.x - 0.5) * printAreaWidth;
      const centroidAngle = radiusMmX > 0 ? xOffsetMm / radiusMmX : 0;
      const angleStart = centroidAngle - arcAngle / 2;

      console.log(`CYLINDER: effOffset.x=${effOffset.x.toFixed(2)} → centroidAngle=${(centroidAngle * 180 / Math.PI).toFixed(1)}deg`);

      // Verify dimensions
      const arcLength3D = radius * arcAngle;
      console.log(`CYLINDER MESH: meshWidth=${meshWidthMm.toFixed(1)}mm, meshHeight=${meshHeightMm.toFixed(1)}mm`);
      console.log(`CYLINDER MESH: arcAngle=${(arcAngle * 180 / Math.PI).toFixed(1)}deg (max 360°), arcLength=${arcLength3D.toFixed(4)} 3D units`);
      console.log(`CYLINDER MESH: radius=${radius.toFixed(4)}, circumference=${(2 * Math.PI * radius).toFixed(4)} 3D units`);

      // Build geometry like PHP version (lines 2684-2721)
      const segmentsH = 64; // More segments for smoother curve
      const segmentsV = 2;
      const vertices: number[] = [];
      const uvs: number[] = [];
      const indices: number[] = [];

      // Find the centroid marker for this technique's position to get correct Y
      const techniquePosition = technique?.position?.toLowerCase() || 'midden';
      const centroidMarker = centroidMarkersRef.current.find(
        m => m.name === `centroid_${techniquePosition}`
      );

      // Use centroid marker Y position if available, otherwise fall back to printableYCenter
      let baseCentroidY: number;
      if (centroidMarker) {
        baseCentroidY = centroidMarker.position.y;
        console.log(`Using centroid marker Y for "${techniquePosition}": ${baseCentroidY.toFixed(4)}`);
      } else {
        // Geen marker voor deze positie: midden van de printzone, maar mét dezelfde
        // verlaging - anders staat het logo te hoog (zie FALLBACK_DROP_MM).
        const sfmmFb = modelHeightMm > 0 ? modelHeightMm / size.y : 0;
        const dropFb = sfmmFb > 0 ? FALLBACK_DROP_MM / sfmmFb : 0;
        baseCentroidY = printableYCenter - dropFb;
        console.log(`No centroid marker for "${techniquePosition}", using printableYCenter: ${baseCentroidY.toFixed(4)}`);
      }

      // "auf Deckel": anker op het midden van de BAND van de dop — het
      // y-bereik waar de dop-node (bijna) zijn maximale straal heeft. De node
      // bevat ook schroefdraad/binnenwerk dat smaller is en lager hangt,
      // waardoor het simpele bbox-midden te laag uitkwam. Gemeten uit de
      // geometrie zelf, dus per model exact; de 2D-pin blijft ongemoeid.
      if (techniquePosition.includes('deckel') && capNodeRef.current) {
        let maxR = 0;
        const punten: { r: number; y: number }[] = [];
        const wp = new THREE.Vector3();
        capNodeRef.current.updateWorldMatrix(true, false);
        capNodeRef.current.traverse((kind) => {
          const g = (kind as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
          const pos = g?.getAttribute ? g.getAttribute('position') : undefined;
          if (!pos) return;
          const stap = Math.max(1, Math.floor(pos.count / 4000)); // sampelen volstaat
          for (let i = 0; i < pos.count; i += stap) {
            wp.fromBufferAttribute(pos as THREE.BufferAttribute, i);
            kind.localToWorld(wp);
            const r = Math.hypot(wp.x, wp.z);
            if (r > maxR) maxR = r;
            punten.push({ r, y: wp.y });
          }
        });
        if (maxR > 0 && punten.length) {
          let bandMin = Infinity, bandMax = -Infinity;
          for (const p of punten) {
            if (p.r >= maxR * 0.93) {
              if (p.y < bandMin) bandMin = p.y;
              if (p.y > bandMax) bandMax = p.y;
            }
          }
          if (bandMax > bandMin) {
            const bandMid = (bandMin + bandMax) / 2;
            console.log(`[3D] Deckel-anker op bandmidden: ${bandMid.toFixed(4)} (band ${bandMin.toFixed(3)}..${bandMax.toFixed(3)}, foto-anker was ${baseCentroidY.toFixed(4)})`);
            baseCentroidY = bandMid;
          }
        }
      }

      // Apply Y offset from modal DURING mesh generation (not after)
      // This ensures the mesh curve matches the cylinder surface at the offset position
      // Zelfde correctie als bij de andere mesh-branch: de verticale verschuiving
      // hoort een fractie van het PRINTVLAK te zijn, niet 30% van het hele model.
      const sfmmY2 = modelHeightMm > 0 ? modelHeightMm / size.y : 0;
      const yOffsetAmount = sfmmY2 > 0
        ? (effOffset.y - 0.5) * (printAreaHeight / sfmmY2)
        : (effOffset.y - 0.5) * size.y * 0.3;
      const centroidY = baseCentroidY - yOffsetAmount; // Subtract because 3D Y is up, 2D Y is down
      console.log(`Cylinder: Y offset=${yOffsetAmount.toFixed(4)}, final centroidY=${centroidY.toFixed(4)}`);

      // Mesh is now correctly sized to match logo aspect ratio - simple 0-1 UV mapping
      for (let v = 0; v <= segmentsV; v++) {
        // Y position centered on centroid - same as PHP line 2696
        const yPos = centroidY - meshHeight3D / 2 + (v / segmentsV) * meshHeight3D;

        for (let h = 0; h <= segmentsH; h++) {
          const angle = angleStart + (h / segmentsH) * arcAngle;
          // PHP uses cos for x and sin for z (lines 2699-2700)
          const x = center.x + radius * Math.cos(angle);
          const z = center.z + radius * Math.sin(angle);

          vertices.push(x, yPos, z);

          // Simple 0-1 UV mapping - mesh aspect ratio matches logo
          // Flip U to show logo on outside
          uvs.push(1 - (h / segmentsH), v / segmentsV);
        }
      }

      // Build indices - PHP uses different winding order (lines 2707-2715)
      for (let v = 0; v < segmentsV; v++) {
        for (let h = 0; h < segmentsH; h++) {
          const a = v * (segmentsH + 1) + h;
          const b = a + 1;
          const c = a + (segmentsH + 1);
          const d = c + 1;
          indices.push(a, b, d);
          indices.push(a, d, c);
        }
      }

      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      console.log(`Created curved mesh: printArea=${meshWidthMm}x${meshHeightMm}mm, circumf=${circumferenceMm.toFixed(0)}mm, arc=${(arcAngle * 180 / Math.PI).toFixed(1)}deg, radius=${radius.toFixed(4)}`);
    } else {
      // Textiel/flat: DECAL-projectie — het logo wordt op het mesh-oppervlak
      // geprojecteerd zodat het meebuigt met de plooien van de stof
      // (i.p.v. een plat vlak dat ervoor zweeft).
      let flatW = logoWidth3D;
      let flatH = logoHeight3D;
      const prodHmm = centroidsData.length > 0 ? centroidsData[0].productHeight : 0;
      if (prodHmm && prodHmm > 0) {
        const sfmm = prodHmm / size.y; // mm per 3D-unit
        // Zelfde valkuil als bij de cilinder-mesh: logoWidthMm/logoHeightMm zijn al
        // in het printvlak gepast met behoud van de verhouding. Geeft de modal een
        // schaal door, dan ZIJN scaleX/scaleY die verhouding al (fracties van het
        // printvlak) en levert nog eens vermenigvuldigen een plat logo op.
        const useModalScale = hasModalScaleRef.current || !!placement?.scale;
        const flatWmm = useModalScale ? printAreaWidth * userScaleX : logoWidthMm * userScaleX;
        // hoogte uit de verhouding van het beeld (zie de opmerking bij de cilinder-mesh)
        const flatHmm = useModalScale ? flatWmm / (logoAspect || 1) : logoHeightMm * userScaleY;
        flatW = flatWmm / sfmm;
        flatH = flatHmm / sfmm;
        console.log(`FLAT MESH: prodH=${prodHmm}mm, sfmm=${sfmm.toFixed(2)}, mesh=${logoWidthMm.toFixed(0)}x${logoHeightMm.toFixed(0)}mm -> ${flatW.toFixed(3)}x${flatH.toFixed(3)} 3D`);
      }

      // Voorkant = dunste horizontale as; doelpunt incl. modal-offsets
      const frontOnX = size.x <= size.z;
      const cfgYd = parseFloat(String(meshConfig?.mesh_position_y)) || 0;
      // 2D-verschuiving = offset binnen het PRINT-VAK (zelfde mm als in 2D),
      // niet een fractie van het hele model — anders wijkt 3D af van 2D.
      const sfmmOff = (centroidsData.length > 0 && centroidsData[0].productHeight > 0)
        ? centroidsData[0].productHeight / size.y : 0;
      const offW = sfmmOff > 0
        ? (effOffset.x - 0.5) * (printAreaWidth / sfmmOff)
        : (effOffset.x - 0.5) * (frontOnX ? size.z : size.x) * 0.3;
      const offYd = sfmmOff > 0
        ? (effOffset.y - 0.5) * (printAreaHeight / sfmmOff)
        : (effOffset.y - 0.5) * size.y * 0.3;

      // Doelpunt uit de POSITIE-CENTROID (px in het 595-canvas) via de
      // foto-bounds uit de config: zo landt 'borst' op de borst en 'voor'
      // midden op het grote borstvlak — elke positie zijn eigen plek.
      const mcAny = meshConfig as any;
      const imgXc = parseFloat(String(mcAny?.svg_image_x)) || 0;
      const imgYc = parseFloat(String(mcAny?.svg_image_y)) || 0;
      const imgWc = parseFloat(String(mcAny?.svg_image_width)) || 0;
      const imgHc = parseFloat(String(mcAny?.svg_image_height)) || 0;
      const tAny = technique as any;
      const cxPx = tAny ? parseFloat(String(tAny.centroid_x_1 ?? tAny.x_position_1)) : NaN;
      const cyPx = tAny ? parseFloat(String(tAny.centroid_y_1 ?? tAny.y_position_1)) : NaN;
      let targetY = printableYCenter + cfgYd - offYd;
      let relW = 0.5;
      if (imgWc > 0 && imgHc > 0 && !isNaN(cxPx) && !isNaN(cyPx)) {
        const relX = (cxPx - imgXc) / imgWc;   // 0..1 links -> rechts op de foto
        const relYv = (cyPx - imgYc) / imgHc;  // 0..1 boven -> onder
        targetY = box.max.y - relYv * size.y + cfgYd - offYd;
        relW = relX;
        console.log(`DECAL doelpunt uit centroid: px=(${cxPx},${cyPx}) rel=(${relX.toFixed(3)},${relYv.toFixed(3)})`);
      }
      const widthAxisSize = frontOnX ? size.z : size.x;
      const widthMin = frontOnX ? box.min.z : box.min.x;
      // Spiegel-correctie: bij frontOnX wijst +breedte-as naar viewer-LINKS,
      // dus foto-relX (0=links) moet geinverteerd worden.
      const relWm = frontOnX ? (1 - relW) : relW;
      const wCoord = widthMin + relWm * widthAxisSize + (frontOnX ? -offW : offW);

      // Grootste mesh in het model als projectie-doel
      let targetMesh: THREE.Mesh | null = null;
      let bestA = 0;
      modelRef.current.traverse((o: any) => {
        if (o.isMesh) {
          const b = new THREE.Box3().setFromObject(o);
          const s = b.getSize(new THREE.Vector3());
          const a = s.x * s.y + s.y * s.z + s.x * s.z;
          if (a > bestA) { bestA = a; targetMesh = o; }
        }
      });

      if (targetMesh) {
        // Positie-bewuste projectie: voor/borst = van voren, achter = van achteren,
        // mouw-links/rechts = ZIJWAARTS op de arm.
        const posName = String((tAny && tAny.position) || '').toLowerCase();
        // NL (Spranz/newwave) + EN positiecodes (XD Connects: 'sleeve left', 'item back', 'Inside neck')
        const isMouw = posName.includes('mouw') || posName.includes('sleeve');
        const isAchter = posName.includes('achter') || posName.split(/[^a-z]+/).includes('back') || posName.includes('neck');
        let rayOrigin: THREE.Vector3;
        let rayDir: THREE.Vector3;
        if (isMouw) {
          const isLinks = posName.includes('links') || posName.includes('left');
          const sleevePlus = frontOnX ? !isLinks : isLinks; // drager-links = viewer-rechts
          const wMax = frontOnX ? box.max.z : box.max.x;
          const wMin = frontOnX ? box.min.z : box.min.x;
          const depthCenter = frontOnX ? center.x : center.z;
          const start = sleevePlus ? wMax + widthAxisSize : wMin - widthAxisSize;
          const dirSign = sleevePlus ? -1 : 1;
          // Mouw-vak iets hoger op de arm (3D-presentatie; 2D-data blijft leidend)
          const sleeveY = targetY + size.y * 0.05;
          if (frontOnX) {
            rayOrigin = new THREE.Vector3(depthCenter, sleeveY, start);
            rayDir = new THREE.Vector3(0, 0, dirSign);
          } else {
            rayOrigin = new THREE.Vector3(start, sleeveY, depthCenter);
            rayDir = new THREE.Vector3(dirSign, 0, 0);
          }
          console.log(`DECAL mouw (${posName}): zijwaartse projectie, kant=${sleevePlus ? '+' : '-'}`);
        } else if (isAchter) {
          // rugfoto is het spiegelbeeld van de voorkant: foto-links = drager-rechts
          const wBack = widthMin + (1 - relWm) * widthAxisSize + (frontOnX ? offW : -offW);
          rayOrigin = frontOnX
            ? new THREE.Vector3(box.min.x - size.x, targetY, wBack)
            : new THREE.Vector3(wBack, targetY, box.min.z - size.z);
          rayDir = frontOnX ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
        } else {
          rayOrigin = frontOnX
            ? new THREE.Vector3(box.max.x + size.x, targetY, wCoord)
            : new THREE.Vector3(wCoord, targetY, box.max.z + size.z);
          rayDir = frontOnX ? new THREE.Vector3(-1, 0, 0) : new THREE.Vector3(0, 0, -1);
        }
        const rc = new THREE.Raycaster(rayOrigin, rayDir);
        const hits = rc.intersectObject(targetMesh, true);
        if (hits.length > 0) {
          const hit = hits[0];
          const hitMesh = hit.object as THREE.Mesh;
          const helper = new THREE.Object3D();
          helper.position.copy(hit.point);
          const n = hit.face
            ? hit.face.normal.clone().transformDirection(hitMesh.matrixWorld)
            : rayDir.clone().negate();
          helper.lookAt(hit.point.clone().add(n));
          // Projectie-diepte gekoppeld aan de logo-maat: diep genoeg voor plooien,
          // maar niet zo diep dat de decal om randen heen wikkelt (mouw-smeer).
          const decalSize = new THREE.Vector3(flatW, flatH, Math.max(flatW, flatH) * 1.2);
          geometry = new DecalGeometry(hitMesh, hit.point, helper.rotation, decalSize);
          isDecal = true;
          console.log(`DECAL op stof: hit=(${hit.point.x.toFixed(3)}, ${hit.point.y.toFixed(3)}, ${hit.point.z.toFixed(3)}), size=${flatW.toFixed(3)}x${flatH.toFixed(3)}`);
          // Camera naar de bedrukte kant (rug/mouw), alleen bij een ANDERE kijkrichting zodat
          // handmatig draaien niet bij elke maat-/positiewijziging wordt overschreven.
          if (!placement?.append && cameraRef.current && controlsRef.current) {
            const viewKey = [Math.sign(rayDir.x), Math.sign(rayDir.z)].join(',');
            if (decalViewRef.current !== viewKey) {
              decalViewRef.current = viewKey;
              const cam = cameraRef.current, ctr = controlsRef.current as any;
              const tgt: THREE.Vector3 = ctr.target ? ctr.target.clone() : center.clone();
              const hd = Math.hypot(cam.position.x - tgt.x, cam.position.z - tgt.z) || 1;
              const d = rayDir.clone().negate(); d.y = 0; d.normalize();
              const camY = cam.position.y;
              cam.position.set(tgt.x + d.x * hd, camY, tgt.z + d.z * hd);
              cam.lookAt(tgt);
              if (ctr.update) ctr.update();
            }
          }
        }
      }
      if (!isDecal) {
        // Vangnet: plat vlak als de projectie geen raakpunt vindt
        geometry = new THREE.PlaneGeometry(flatW, flatH);
      }
    }

    // Portrait rotation is handled by swapping mesh dimensions in mesh generation
    // This ensures the logo appears rotated 90° CW on portrait print areas
    // No texture rotation needed
    texture.center.set(0.5, 0.5);
    texture.rotation = 0;
    texture.needsUpdate = true;

    // Check if technique is engraving (L* codes like L1, L2, etc.)
    const isEngraving = (technique?.technique_code || selectedTechnique || '').startsWith('L');

    // For engraving, convert the texture to grayscale
    let materialTexture = texture;
    if (isEngraving && texture.image) {
      console.log(`Engraving technique detected (${selectedTechnique}) - converting to grayscale`);

      // Create a canvas to convert the image to grayscale
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = texture.image.width;
        canvas.height = texture.image.height;
        ctx.drawImage(texture.image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Convert to grayscale
        for (let i = 0; i < data.length; i += 4) {
          const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
          data[i] = gray;     // R
          data[i + 1] = gray; // G
          data[i + 2] = gray; // B
          // Alpha (data[i + 3]) stays the same
        }

        ctx.putImageData(imageData, 0, 0);

        // Create new texture from grayscale canvas
        materialTexture = new THREE.CanvasTexture(canvas);
        materialTexture.colorSpace = THREE.SRGBColorSpace;
        // Copy rotation settings from original texture
        materialTexture.center.copy(texture.center);
        materialTexture.rotation = texture.rotation;
        materialTexture.needsUpdate = true;
      }
    }

    // Create material with logo texture
    const material = new THREE.MeshStandardMaterial({
      map: materialTexture,
      transparent: true,
      side: THREE.DoubleSide,
      alphaTest: 0.1,
      metalness: isEngraving ? 0.2 : 0.0, // Slight metalness for engraved look
      roughness: isEngraving ? 0.6 : 0.5
    });

    if (isDecal) {
      material.polygonOffset = true;
      material.polygonOffsetFactor = -4;
      material.depthWrite = false;
    }

    const logoMesh = new THREE.Mesh(geometry, material);

    // Position logo mesh in the printable area
    const yPos = printableYCenter + (parseFloat(String(meshConfig?.mesh_position_y)) || 0);

    if ((meshType === 'curved') && markerPoints && markerPoints.length >= 10) {
      // Curved mesh from markers is already positioned at correct location
      logoMesh.position.set(
        parseFloat(String(meshConfig?.mesh_position_x)) || 0,
        parseFloat(String(meshConfig?.mesh_position_y)) || 0,
        parseFloat(String(meshConfig?.mesh_position_z)) || 0
      );
      // Apply scale
      const scaleW = parseFloat(String(meshConfig?.mesh_scale_width)) || 1;
      const scaleH = parseFloat(String(meshConfig?.mesh_scale_height)) || 1;
      logoMesh.scale.set(scaleW, scaleH, scaleW);
      // Apply rotation
      logoMesh.rotation.x = (parseFloat(String(meshConfig?.mesh_rotation_x)) || 0) * Math.PI / 180;
      logoMesh.rotation.y = (parseFloat(String(meshConfig?.mesh_rotation_y)) || 0) * Math.PI / 180;
      logoMesh.rotation.z = (parseFloat(String(meshConfig?.mesh_rotation_z)) || 0) * Math.PI / 180;
    } else if (meshType === 'cylinder' || meshType === 'curved') {
      // Curved geometry already has Y position baked in - only apply optional mesh_position offsets
      logoMesh.position.set(
        parseFloat(String(meshConfig?.mesh_position_x)) || 0,
        parseFloat(String(meshConfig?.mesh_position_y)) || 0,
        parseFloat(String(meshConfig?.mesh_position_z)) || 0
      );
      console.log(`Logo curved mesh offset: x=${logoMesh.position.x.toFixed(3)}, y=${logoMesh.position.y.toFixed(3)}, z=${logoMesh.position.z.toFixed(3)}`);
    } else if (!isDecal) {
      // For flat products (vangnet-plane): de voorkant is de max-zijde van de DUNSTE horizontale as
      // (een shirt is breed maar dun; GLB-modellen kunnen langs X of Z kijken).
      const cfgOffA = parseFloat(String(meshConfig?.mesh_position_x)) || 0;
      const cfgOffZ = parseFloat(String(meshConfig?.mesh_position_z)) || 0;
      const rotYCfg = (parseFloat(String(meshConfig?.mesh_rotation_y)) || 0) * Math.PI / 180;
      if (size.x <= size.z) {
        // dikte langs X -> vlak op +X, kijkend langs +X
        logoMesh.position.set(box.max.x + 0.002 + cfgOffZ, yPos, center.z + cfgOffA);
        logoMesh.rotation.y = Math.PI / 2 + rotYCfg;
      } else {
        // dikte langs Z -> vlak op +Z (standaard)
        logoMesh.position.set(center.x + cfgOffA, yPos, box.max.z + 0.002 + cfgOffZ);
        logoMesh.rotation.y = rotYCfg;
      }
      logoMesh.rotation.x = (parseFloat(String(meshConfig?.mesh_rotation_x)) || 0) * Math.PI / 180;
      logoMesh.rotation.z = (parseFloat(String(meshConfig?.mesh_rotation_z)) || 0) * Math.PI / 180;
    }

    // Apply position offset from modal (0-1 range, 0.5 = centered)
    // For cylinder/curved meshes: X offset is handled via angle rotation in mesh generation
    // For flat products: use linear X offset
    const isCylinder = meshType === 'cylinder' || meshType === 'curved' || meshType === 'marker';

    if (!isCylinder && !isDecal) {
      // Only apply linear X offset for flat products — langs de BREEDTE-as van het model
      if (size.x <= size.z) {
        const offsetW = (effOffset.x - 0.5) * size.z * 0.3;
        logoMesh.position.z += offsetW;
        console.log(`[3D] Flat product breedte-offset (z-as): ${offsetW.toFixed(4)}`);
      } else {
        const offsetX = (effOffset.x - 0.5) * size.x * 0.3;
        logoMesh.position.x += offsetX;
        console.log(`[3D] Flat product X offset: ${offsetX.toFixed(4)}`);
      }
    }

    // Y offset for cylinder meshes is applied DURING mesh generation (in centroidY calculation)
    // For flat products, apply Y offset after mesh creation
    if (!isCylinder && !isDecal) {
      const offsetY = (effOffset.y - 0.5) * size.y * 0.3;
      logoMesh.position.y -= offsetY;
      console.log(`[3D] Flat product Y offset: ${offsetY.toFixed(4)}`);
    }

    console.log(`[3D] Logo positioned (from modal: ${effOffset.x.toFixed(2)}, ${effOffset.y.toFixed(2)})`);

    sceneRef.current.add(logoMesh);
    // "auf Deckel": logo aan de geanimeerde dop-node hangen zodat hij met de
    // Open/Close-animatie meebeweegt. attach() behoudt de wereldpositie.
    const posNaamCap = String(technique?.position || '').toLowerCase();
    if (posNaamCap.includes('deckel') && capNodeRef.current) {
      capNodeRef.current.attach(logoMesh);
      console.log(`[3D] Logo aan dop-node "${capNodeRef.current.name}" gehangen (positie "${posNaamCap}")`);
    }
    if (placement?.append) {
      extraLogoMeshesRef.current.push(logoMesh);
    } else {
      logoMeshRef.current = logoMesh;
      setMainMeshVersion(v => v + 1);
    }

    const techniqueInfo = technique ? ` voor ${technique.technique_code} (${technique.print_area})` : '';
    console.log(`Applied logo to model${techniqueInfo}: ${logoWidthMm.toFixed(0)}x${logoHeightMm.toFixed(0)}mm`);
  }, [meshConfig, techniques, selectedTechnique, centroidsData, surfaceOffset, logoScale, logoOffset]);

  // Re-apply logo when technique changes (hotswap), surfaceOffset changes, scale or position changes
  useEffect(() => {
    if (logoTexture && selectedTechnique && (centroidsData.length > 0 || String((meshConfigRef.current as any)?.mesh_type) === 'imprint')) {
      console.log(`Re-applying logo: technique=${selectedTechnique}, surfaceOffset=${surfaceOffset}, scale=${logoScale.scaleX.toFixed(2)}x${logoScale.scaleY.toFixed(2)}, offset=${logoOffset.x.toFixed(2)}x${logoOffset.y.toFixed(2)}`);
      // herbouw-ronde meldt bezig: loader blijft dicht tot ALLES staat
      try { window.parent.postMessage({ type: 'magic_3d_loading' }, '*'); } catch (e) { /* geen parent */ }
      applyLogoToModel(logoTexture);
    }
  }, [selectedTechnique, surfaceOffset, applyLogoToModel, logoTexture, centroidsData, logoScale, logoOffset]);

  // "Toon alles": extra decals voor de overige gekozen posities. Draait na
  // elke hoofd-decal-rebuild (mainMeshVersion) zodat kleurwissel/verschuiven
  // de extra logo's niet kwijtraakt.
  useEffect(() => {
    let cancelled = false;
    const loadTex = (url: string): Promise<THREE.Texture> => {
      const cached = extraTextureCacheRef.current.get(url);
      if (cached) return Promise.resolve(cached);
      return new Promise((resolve, reject) => {
        new THREE.TextureLoader().load(url, (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.magFilter = THREE.LinearFilter;
          extraTextureCacheRef.current.set(url, tex);
          if (extraTextureCacheRef.current.size > 12) {
            const first = extraTextureCacheRef.current.keys().next().value as string;
            extraTextureCacheRef.current.get(first)?.dispose();
            extraTextureCacheRef.current.delete(first);
          }
          resolve(tex);
        }, undefined, reject);
      });
    };
    const run = async () => {
      if (!sceneRef.current) return;
      // Identieke extras overslaan: bij een kleurwissel verandert alleen de
      // centroids-array-identiteit, niet de inhoud - de decals blijven geldig.
      const extrasSig = JSON.stringify(extraPlacements.map(p => [p.techniqueId, p.sizePct, (p.logoDataUrl || '').length])) + '|' + mainMeshVersion + '|' + (logoTexture ? 1 : 0);
      if (extrasSig === lastExtrasSigRef.current && extraLogoMeshesRef.current.length > 0) {
        try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
        return;
      }
      lastExtrasSigRef.current = extrasSig;
      for (const m of extraLogoMeshesRef.current) {
        sceneRef.current.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      extraLogoMeshesRef.current = [];
      if (!logoTexture || extraPlacements.length === 0 || centroidsData.length === 0) {
        try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
        return;
      }
      for (const p of extraPlacements) {
        if (String(p.techniqueId) === String(selectedTechnique)) continue; // hoofddecal dekt de actieve positie al
        const row = techniques.find(t => String(t.id) === String(p.techniqueId));
        if (!row) continue;
        // Eigen artwork per positie: laad de eigen texture, anders het hoofdlogo
        let tex = logoTexture;
        if (p.logoDataUrl) {
          try { tex = await loadTex(p.logoDataUrl); } catch { tex = logoTexture; }
        }
        if (cancelled) return;
        applyLogoToModel(tex, {
          technique: row,
          scale: { scaleX: p.sizePct, scaleY: p.sizePct },
          offset: { x: 0.5, y: 0.5 },
          append: true,
        });
      }
      if (!cancelled) {
        // alle decals staan -> loader kan weg
        try { window.parent.postMessage({ type: 'magic_3d_ready' }, '*'); } catch (e) { /* geen parent */ }
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraPlacements, mainMeshVersion, logoTexture, techniques, selectedTechnique, centroidsData]);

  // Auto-apply logo from localStorage after model loads
  useEffect(() => {
    if (!loading && pendingLogo && (centroidsData.length > 0 || String((meshConfigRef.current as any)?.mesh_type) === 'imprint') && techniques.length > 0 && !logoTexture) {
      console.log('Auto-applying logo from localStorage');
      try { window.parent.postMessage({ type: 'magic_3d_loading' }, '*'); } catch (e) { /* geen parent */ }

      // Set technique if provided
      const posRow = (positionFromUrl && pendingTechnique)
        ? techniques.find(t => t.technique_code === pendingTechnique && String(t.position).toLowerCase() === String(positionFromUrl).toLowerCase())
        : null;
      // rij die straks geldt (state-update is in deze tick nog niet zichtbaar in applyLogoToModel)
      const chosenRow = posRow
        || (pendingTechnique ? techniques.find(t => String(t.id) === String(pendingTechnique) || t.technique_code === pendingTechnique) : undefined)
        || (!selectedTechnique && techniques.length > 0 ? techniques[0] : undefined);
      if (posRow) {
        setSelectedTechnique(String(posRow.id));
      } else if (pendingTechnique && techniques.some(t => String(t.id) === String(pendingTechnique) || t.technique_code === pendingTechnique)) {
        setSelectedTechnique(pendingTechnique);
      } else if (techniques.length > 0 && !selectedTechnique) {
        // Default to first technique if none selected
        setSelectedTechnique(techniques[0].technique_code);
      }

      // Create texture from base64 logo and apply
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(pendingLogo, (texture) => {
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = true;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        const maxAnisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
        texture.anisotropy = maxAnisotropy;
        texture.premultiplyAlpha = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;

        setLogoTexture(texture);
        applyLogoToModel(texture, chosenRow ? { technique: chosenRow } : undefined);
        setStatus('Logo toegepast vanuit modal');

        // Set ready flags for screenshot service (with logo applied)
        (window as any).__3DReady = true;
        (window as any).__3DLogoApplied = true;
        console.log('[3D] Ready flag set (logo applied)');

        // Auto-screenshot: capture and POST to callback URL
        if (autoScreenshot && rendererRef.current && sceneRef.current && cameraRef.current) {
          console.log('[3D] AutoScreenshot: capturing...');
          // Wait a frame for render to complete
          setTimeout(() => {
            try {
              rendererRef.current!.render(sceneRef.current!, cameraRef.current!);
              const dataUrl = rendererRef.current!.domElement.toDataURL('image/png');
              console.log('[3D] AutoScreenshot: captured', dataUrl.length, 'bytes');

              // Get callback URL from params
              const callbackUrl = urlParams.get('callback');
              const transactionId = urlParams.get('transaction_id');

              if (callbackUrl) {
                // POST to callback
                fetch(callbackUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    success: true,
                    screenshot: dataUrl,
                    sku: skuFromUrl,
                    technique: techniqueFromUrl,
                    transaction_id: transactionId
                  })
                }).then(r => {
                  console.log('[3D] AutoScreenshot: posted to callback', r.status);
                }).catch(e => {
                  console.error('[3D] AutoScreenshot: callback failed', e);
                });
              } else {
                // No callback - expose via window for Puppeteer
                (window as any).__3DScreenshot = dataUrl;
                console.log('[3D] AutoScreenshot: stored in window.__3DScreenshot');
              }
            } catch (err) {
              console.error('[3D] AutoScreenshot error:', err);
            }
          }, 500);
        }

        // Clear pending logo after applying
        setPendingLogo(null);
        setPendingTechnique(null);
      });
    }
  }, [loading, pendingLogo, pendingTechnique, centroidsData, techniques, logoTexture, selectedTechnique, applyLogoToModel]);

  // Remove logo (removeFromParent: dop-mesh hangt aan de dop-node)
  const removeLogo = () => {
    if (logoMeshRef.current && sceneRef.current) {
      logoMeshRef.current.removeFromParent();
      logoMeshRef.current.geometry.dispose();
      (logoMeshRef.current.material as THREE.Material).dispose();
      logoMeshRef.current = null;
    }
    if (sceneRef.current) {
      for (const m of extraLogoMeshesRef.current) {
        m.removeFromParent();
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      extraLogoMeshesRef.current = [];
    }
    if (logoTexture) {
      logoTexture.dispose();
      setLogoTexture(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setStatus('Logo verwijderd');
  };

  // Handle GLB model upload
  const handleGlbUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.glb')) {
      setStatus('Alleen .glb bestanden toegestaan');
      return;
    }

    setUploadingGlb(true);
    setStatus(`Uploading ${file.name}...`);

    try {
      const arrayBuffer = await file.arrayBuffer();

      const uploadUrl = `/api/3d/upload-model?filename=${encodeURIComponent(file.name)}&supplier=spranz${selectedSku ? `&sku=${encodeURIComponent(selectedSku)}` : ''}`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: arrayBuffer,
      });

      const result = await response.json();

      if (result.success) {
        const skuMsg = result.sku ? ` (SKU: ${result.sku})` : '';
        setStatus(`Model geüpload: ${result.path}${skuMsg}`);
        console.log('GLB uploaded:', result);
        // Refresh products list so the new model appears
        setProductsVersion(v => v + 1);
      } else {
        setStatus(`Upload fout: ${result.error}`);
      }
    } catch (error: any) {
      console.error('GLB upload error:', error);
      setStatus(`Upload fout: ${error.message}`);
    } finally {
      setUploadingGlb(false);
      if (glbInputRef.current) {
        glbInputRef.current.value = '';
      }
    }
  };

  // Cap-animatie: doeltijd voor een frame als fractie van de clip-duur
  // (fps-onafhankelijk; frame 1 -> t=0, frame CAP_TOTAL_FRAMES -> clip-einde)
  const capTimeForFrame = (frame: number) => {
    const dur = capClipDurRef.current || 0;
    return ((frame - 1) / (CAP_TOTAL_FRAMES - 1)) * dur;
  };
  // Open: speel frame 1 -> 68. Sluit: speel terug 68 -> frame 1 (dop schroeft terug).
  const toggleCap = () => {
    const actie = capActionRef.current;
    if (!actie || capBusy) return;
    clockRef.current.getDelta(); // delta resetten zodat de eerste frame niet verspringt
    actie.paused = false;
    if (!capOpen) {
      actie.timeScale = 1;
      capTargetRef.current = capTimeForFrame(CAP_OPEN_FRAME);
      setCapOpen(true);
    } else {
      actie.timeScale = -1;
      capTargetRef.current = 0;
      setCapOpen(false);
    }
    setCapBusy(true);
  };
  const capButtonLabel = capBusy ? '…' : (capOpen ? 'Close' : 'Open');

  // Hydroglasur (HG) laag-menu — verschijnt alleen als de gekozen techniek HG is.
  const currentTechnique = techniques.find(t => String(t.id) === String(selectedTechnique))
    || techniques.find(t => t.technique_code === selectedTechnique);
  const isHydroglasur = !!currentTechnique
    && (currentTechnique.technique_code === 'HG' || /hydroglas/i.test(currentTechnique.technique_name || ''));
  const HG_FILL_LABELS: Record<HgFill, string> = {
    fullcolor: 'Full color', pantone: 'Pantone', glitter: 'Glitter', goud: 'Goud', metallic: 'Metallic',
  };
  const addHgLayer = () => setHgLayers(ls =>
    ls.length >= 8 ? ls : [...ls, { id: Math.max(0, ...ls.map(l => l.id)) + 1, fill: 'fullcolor' }]);
  const removeHgLayer = (id: number) => setHgLayers(ls => ls.length <= 1 ? ls : ls.filter(l => l.id !== id));
  // Oude testversie van het lagenmenu. Het echte lagenpaneel is
  // HydroglasurDrawer.tsx in de modal; dit paneel kwam daar overheen te staan in
  // de 3D-preview zodra je HG koos. Uitgezet, niet verwijderd — zet deze vlag op
  // true om hem terug te halen.
  const TOON_HG_TESTPANEEL = false;
  const hgPanel = (TOON_HG_TESTPANEEL && isHydroglasur) ? (
    <aside className="hg-panel" style={{
      position: 'absolute', left: 16, top: 80, width: 264, zIndex: 20,
      background: 'rgba(20,20,35,0.92)', color: '#fff', borderRadius: 12,
      padding: '14px 16px', boxShadow: '0 4px 18px rgba(0,0,0,0.45)', fontSize: 14,
    }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>Hydroglasur</div>
      <div style={{ opacity: 0.7, fontSize: 12, margin: '2px 0 10px' }}>Full-color print · lagen (max 8)</div>
      {hgLayers.map((l, i) => (
        <div key={l.id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '8px 10px', marginBottom: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 8,
        }}>
          <span style={{ opacity: 0.6, fontSize: 12 }}>Laag {i + 1}</span>
          <span style={{ fontWeight: 600, flex: 1, textAlign: 'center' }}>{HG_FILL_LABELS[l.fill]}</span>
          <button onClick={() => removeHgLayer(l.id)} disabled={hgLayers.length <= 1} title="Verwijder laag"
            style={{
              border: 'none', background: 'transparent', color: '#fff', fontSize: 16, lineHeight: 1,
              cursor: hgLayers.length <= 1 ? 'default' : 'pointer', opacity: hgLayers.length <= 1 ? 0.3 : 0.85,
            }}>×</button>
        </div>
      ))}
      <button onClick={addHgLayer} disabled={hgLayers.length >= 8} style={{
        marginTop: 4, width: '100%', padding: 8, borderRadius: 8, color: '#fff',
        border: '1px dashed rgba(255,255,255,0.3)', background: 'transparent',
        cursor: hgLayers.length >= 8 ? 'default' : 'pointer', opacity: hgLayers.length >= 8 ? 0.4 : 1,
      }}>+ Laag toevoegen</button>
    </aside>
  ) : null;

  // Embed mode - full screen viewer only
  if (isEmbedMode) {
    return (
      <div className="embed-container">
        <div ref={containerRef} className="viewer-canvas" />
        {hgPanel}
        {/* cap-knop in embed alleen met ?cap=1 (full-screen presentatie); zo blijft hij
            weg uit het kleine 3D-paneel in de modal */}
        {hasCap && showCapInEmbed && (
          <button
            onClick={toggleCap}
            disabled={capBusy}
            className="cap-toggle-btn"
            style={{
              position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)',
              padding: '10px 24px', border: 'none', borderRadius: 24, cursor: capBusy ? 'default' : 'pointer',
              background: '#16a34a', color: '#fff', fontSize: 15, fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)', opacity: capBusy ? 0.6 : 1, zIndex: 10,
            }}
          >
            {capButtonLabel}
          </button>
        )}
        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            <p>Model laden...</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="viewer-container">
      {/* Header */}
      <header className="viewer-header">
        <h1>Magic 3D Viewer</h1>
        <div className="header-controls">
          <select
            value={selectedSku}
            onChange={e => handleProductSelect(e.target.value)}
            className="product-selector"
          >
            <option value="">Selecteer een product...</option>
            {products.map(p => (
              <option key={p.variant_sku} value={p.variant_sku}>
                {p.variant_sku} - {p.product_name || 'Onbekend product'}
              </option>
            ))}
          </select>

          {techniques.length > 0 && (
            <select
              value={selectedTechnique}
              onChange={e => handleTechniqueSelect(e.target.value)}
              className="technique-selector"
            >
              <option value="">Selecteer techniek...</option>
              {techniques.map(t => (
                <option key={String(t.id)} value={String(t.id)}>
                  {t.technique_name || t.technique_code} {t.position ? `- ${t.position} ` : ''}({t.print_area})
                </option>
              ))}
            </select>
          )}

          {/* Logo upload */}
          {selectedSku && (
            <div className="logo-upload-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="upload-btn">
                Upload Logo
              </label>
              {logoTexture && (
                <button onClick={removeLogo} className="remove-logo-btn">
                  Verwijder
                </button>
              )}
            </div>
          )}

          {/* GLB Model upload */}
          <div className="glb-upload-controls">
            <input
              ref={glbInputRef}
              type="file"
              accept=".glb"
              onChange={handleGlbUpload}
              style={{ display: 'none' }}
              id="glb-upload"
            />
            <label htmlFor="glb-upload" className={`upload-btn glb-upload-btn ${uploadingGlb ? 'uploading' : ''}`}>
              {uploadingGlb ? 'Uploading...' : 'Upload GLB Model'}
            </label>
          </div>
        </div>
        <span className="status">{status}</span>
      </header>

      {/* 3D Canvas */}
      <main className="viewer-main">
        <div ref={containerRef} className="viewer-canvas" />
        {hgPanel}
        {hasCap && (
          <button
            onClick={toggleCap}
            disabled={capBusy}
            className="cap-toggle-btn"
            style={{
              position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
              padding: '10px 24px', border: 'none', borderRadius: 24, cursor: capBusy ? 'default' : 'pointer',
              background: '#16a34a', color: '#fff', fontSize: 15, fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)', opacity: capBusy ? 0.6 : 1, zIndex: 10,
            }}
          >
            {capButtonLabel}
          </button>
        )}
        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            <p>Model laden...</p>
          </div>
        )}
      </main>

      {/* Info Panel */}
      {meshConfig && (
        <aside className="info-panel">
          <h3>Configuratie</h3>
          <div className="info-row">
            <span>Mesh Type:</span>
            <span>{meshConfig.mesh_type}</span>
          </div>
          <div className="info-row">
            <span>Scale:</span>
            <span>{parseFloat(String(meshConfig.scale || 1)).toFixed(2)}</span>
          </div>
          <div className="info-row">
            <span>Techniques:</span>
            <span>{techniques.length}</span>
          </div>
          <div className="info-row">
            <span>Centroids:</span>
            <span>{centroidMarkersRef.current.length}</span>
          </div>
          <div className="info-row slider-row">
            <span>Surface Offset:</span>
            <span>{surfaceOffset.toFixed(3)}</span>
          </div>
          <input
            type="range"
            min="-0.02"
            max="0.02"
            step="0.001"
            value={surfaceOffset}
            onChange={(e) => setSurfaceOffset(parseFloat(e.target.value))}
            className="offset-slider"
          />
        </aside>
      )}
    </div>
  );
}

export default App;
